import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { contrattiApi, immobiliApi, usersApi, tipiDocumentoApi, contractsApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Combobox, ComboboxMulti } from '../../components/ui/combobox';
import { 
  Plus, Search, Edit2, Trash2, FileText, Calendar, Euro, Home, User, Ban, RotateCcw,
  Users, FileSignature, UserPlus, Upload, Download, CreditCard, Check, AlertCircle, FileDown, FileSpreadsheet, Hash, Building2,
  ArrowUpDown, ArrowUp, ArrowDown, Lock, Unlock, History, Save, CheckCircle, Shield, ChevronDown, ChevronRight, FileX, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Mini-cronologia delle modifiche ai codici Mandato/Contratto Servizio (legge da audit_trail).
// Read-only, collassato di default, max 20 entry.
const CodiciHistoryPanel = ({ contractId }) => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/contracts/${contractId}/codici-history?limit=20`);
      setEntries(res.data?.entries || []);
    } catch (e) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  const handleToggle = () => {
    setOpen(prev => {
      const next = !prev;
      if (next) { loadHistory(); }
      return next;
    });
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-200" data-testid="codici-history-panel">
      <button
        type="button"
        className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900"
        onClick={handleToggle}
        data-testid="codici-history-toggle"
      >
        <History className="w-3 h-3" />
        Cronologia modifiche codici
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-2">
          {loading && <p className="text-xs text-slate-400">Caricamento...</p>}
          {!loading && entries.length === 0 && (
            <p className="text-xs text-slate-400">Nessuna modifica registrata.</p>
          )}
          {!loading && entries.length > 0 && (
            <div className="border rounded overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs bg-slate-50">
                    <TableHead className="py-2 font-semibold">Quando</TableHead>
                    <TableHead className="py-2 font-semibold">Campo</TableHead>
                    <TableHead className="py-2 font-semibold">Da</TableHead>
                    <TableHead className="py-2 font-semibold">A</TableHead>
                    <TableHead className="py-2 font-semibold">Chi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e, idx) => (
                    <TableRow key={`${e.timestamp}-${e.campo}-${idx}`} className="text-xs">
                      <TableCell className="py-1.5 whitespace-nowrap">
                        {e.timestamp ? new Date(e.timestamp).toLocaleString('it-IT') : '-'}
                      </TableCell>
                      <TableCell className="py-1.5">{e.campo_label || e.campo}</TableCell>
                      <TableCell className="py-1.5 font-mono text-slate-500">{e.vecchio_codice || '-'}</TableCell>
                      <TableCell className="py-1.5 font-mono font-semibold">{e.nuovo_codice || '-'}</TableCell>
                      <TableCell className="py-1.5 text-slate-600">{e.modificato_da}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Mini-cronologia delle modifiche al pagatore SDD (iban_addebito_id). Read-only, collassato.
const IbanHistoryPanel = ({ contractId }) => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/contracts/${contractId}/iban-history?limit=20`);
      setEntries(res.data?.entries || []);
    } catch (e) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  const handleToggle = () => {
    setOpen(prev => {
      const next = !prev;
      if (next) { loadHistory(); }
      return next;
    });
  };

  return (
    <div className="mt-3" data-testid="iban-history-panel">
      <button
        type="button"
        className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900"
        onClick={handleToggle}
        data-testid="iban-history-toggle"
      >
        <History className="w-3 h-3" />
        Cronologia modifiche pagatore SDD
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-2">
          {loading && <p className="text-xs text-slate-400">Caricamento...</p>}
          {!loading && entries.length === 0 && (
            <p className="text-xs text-slate-400">Nessuna modifica registrata.</p>
          )}
          {!loading && entries.length > 0 && (
            <div className="border rounded overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs bg-slate-50">
                    <TableHead className="py-2 font-semibold">Quando</TableHead>
                    <TableHead className="py-2 font-semibold">Da</TableHead>
                    <TableHead className="py-2 font-semibold">A</TableHead>
                    <TableHead className="py-2 font-semibold">Pagamenti sync</TableHead>
                    <TableHead className="py-2 font-semibold">Chi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e, idx) => (
                    <TableRow key={`${e.timestamp}-${idx}`} className="text-xs">
                      <TableCell className="py-1.5 whitespace-nowrap">
                        {e.timestamp ? new Date(e.timestamp).toLocaleString('it-IT') : '-'}
                      </TableCell>
                      <TableCell className="py-1.5 text-slate-500">{e.vecchio_pagatore}</TableCell>
                      <TableCell className="py-1.5 font-semibold">
                        {e.nuovo_pagatore}
                        {e.nuovo_iban && <div className="font-mono text-[10px] text-slate-500">{e.nuovo_iban}</div>}
                      </TableCell>
                      <TableCell className="py-1.5 text-center">
                        {e.pagamenti_aggiornati !== null && e.pagamenti_aggiornati !== undefined ? (
                          <span className="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">{e.pagamenti_aggiornati}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="py-1.5 text-slate-600">{e.modificato_da}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ContrattiPage = () => {
  const [contratti, setContratti] = useState([]);
  const [immobili, setImmobili] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStato, setFilterStato] = useState('attivo');
  const [showDialog, setShowDialog] = useState(false);
  const [showDocumentiDialog, setShowDocumentiDialog] = useState(false);
  const [showSubentroDialog, setShowSubentroDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedContratto, setSelectedContratto] = useState(null);
  // Ref sincronizzato con selectedContratto per evitare di rimettere selectedContratto
  // nelle dipendenze di fetchData (causava un loop: digitare data → setSelectedContratto →
  // fetchData ricreato → useEffect rilancia fetchData → setSelectedContratto resetta al DB).
  const selectedContrattoRef = useRef(null);
  useEffect(() => { selectedContrattoRef.current = selectedContratto; }, [selectedContratto]);
  const [documentiContratto, setDocumentiContratto] = useState([]);
  const [tipiDocumento, setTipiDocumento] = useState([]);
  const [mandatoServizio, setMandatoServizio] = useState(null);
  const [contrattoServizio, setContrattoServizio] = useState(null);
  
  // Ordinamento colonne
  const [sortColumn, setSortColumn] = useState('codice_contratto');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Blocco/sblocco dati economici
  const [datiEconomiciSbloccati, setDatiEconomiciSbloccati] = useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const [showTimelineDialog, setShowTimelineDialog] = useState(false);
  const [timelineContratto, setTimelineContratto] = useState([]);
  
  // Cessazione contratto
  const [showCessaDialog, setShowCessaDialog] = useState(false);
  const [cessaContrattoId, setCessaContrattoId] = useState(null);
  const [dataCessazione, setDataCessazione] = useState('');
  const [motivoCessazione, setMotivoCessazione] = useState('');
  
  // Storico Pagamenti
  const [showStoricoPagamentiDialog, setShowStoricoPagamentiDialog] = useState(false);
  const [storicoPagamentiData, setStoricoPagamentiData] = useState(null);
  const [uploadingFattura, setUploadingFattura] = useState(null);
  const [expandedAnnualita, setExpandedAnnualita] = useState({});
  
  // Form data for new/edit contract
  const [formData, setFormData] = useState({
    property_id: '',
    inquilini_ids: [],
    garanti_ids: [],
    iban_bonifico_id: '',  // IBAN proprietario per SEPA
    iban_addebito_id: '',  // IBAN inquilino per SDD
    intestatario_fattura_id: '',  // Conduttore a cui intestare la fattura /CO
    data_inizio: '',
    data_fine: '',
    tipo_contratto: '4+4',
    regime_fiscale: 'cedolare',
    canone_mensile: '',
    spese_condominiali: '0',
    spese_forfettarie: '0',
    quota_semplificaffitto_proprietario_perc: '7',
    quota_semplificaffitto_inquilino_perc: '9',
    sconto_sa_proprietario: '0',
    sconto_sa_inquilino: '0',
    sconto_sa_inquilino_fino_a: '',
    quota_bollo: '2.00',
    mesi_indennizzo: '3',
    note: '',
    prosecuzione_contratto_id: '',
    stato: 'in_compilazione',  // Default: in compilazione
    // Codici manuali per contratti esistenti
    codice_contratto_manuale: '',
    mandato_sdd_codice: '',
    mandato_sdd_data_firma: '',
    contratto_servizio_codice: '',
    data_firma_contratto_servizio: '',
    usa_codici_manuali: false,
    // Codici documenti di servizio (override manuale in fase di transizione)
    mandato_servizio_codice: '',
    mandato_sdd_locatore_codice: '',
    mandato_sdd_conduttore_codice: '',
  });

  // Subentro form
  const [subentroData, setSubentroData] = useState({
    vecchio_inquilino_id: '',
    nuovo_inquilino_id: '',
    data_subentro: '',
    note: ''
  });

  // Upload documento form
  const [uploadDocData, setUploadDocData] = useState({
    tipo_documento: '',
    file: null,
    note: '',
    ufficio_territoriale: '',
    data_registrazione: '',
    protocollo: '',
    codice_identificativo: ''
  });
  // Dati registrazione contratto (editabili)
  const [regData, setRegData] = useState({
    ufficio_territoriale: '',
    data_registrazione: '',
    protocollo: '',
    codice_identificativo: ''
  });
  const [savingReg, setSavingReg] = useState(false);

  // Selected immobile's proprietari
  const [selectedImmobileProprietari, setSelectedImmobileProprietari] = useState([]);
  
  // Dialog per nuovo inquilino inline
  const [showNewInquilinoDialog, setShowNewInquilinoDialog] = useState(false);
  const [newInquilinoData, setNewInquilinoData] = useState({
    nome: '',
    cognome: '',
    email: '',
    codice_fiscale: '',
    iban: '',
    telefono: '',
    pec: '',
    altro_contatto: '',
    data_nascita: '',
    luogo_nascita: '',
    provincia_nascita: '',
    indirizzo_residenza: '',
    cap_residenza: '',
    citta_residenza: '',
    provincia_residenza: ''
  });

  // Dialog per nuovo garante inline
  const [showNewGaranteDialog, setShowNewGaranteDialog] = useState(false);
  const [newGaranteData, setNewGaranteData] = useState({
    nome: '',
    cognome: '',
    email: '',
    codice_fiscale: '',
    iban: '',
    telefono: '',
    pec: '',
    altro_contatto: '',
    data_nascita: '',
    luogo_nascita: '',
    provincia_nascita: '',
    indirizzo_residenza: '',
    cap_residenza: '',
    citta_residenza: '',
    provincia_residenza: ''
  });

  // Suggerimenti comuni per form utenti inline
  const [newUserComuniSuggestions, setNewUserComuniSuggestions] = useState([]);
  const [newUserComuniNascitaSuggestions, setNewUserComuniNascitaSuggestions] = useState([]);

  // Dialog per nuovo immobile inline
  const [showNewImmobileDialog, setShowNewImmobileDialog] = useState(false);
  const [newImmobileData, setNewImmobileData] = useState({
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    proprietari_ids: []
  });
  
  // Autocomplete comuni per nuovo immobile
  const [comuniSuggestions, setComuniSuggestions] = useState([]);
  const [searchingComuni, setSearchingComuni] = useState(false);

  // Calcola le fasi del contratto in base al tipo (4+4, 3+2, 4+2, transitorio)
  const calcolaFasiContratto = (contratto) => {
    if (!contratto.data_inizio || !contratto.tipo_contratto) {
      return null;
    }

    const dataInizio = new Date(contratto.data_inizio);
    const tipoContratto = contratto.tipo_contratto;
    
    // Determina durata prima e seconda fase in mesi
    let mesiFase1 = 0;
    let mesiFase2 = 0;
    
    switch (tipoContratto) {
      case '4+4':
        mesiFase1 = 48;
        mesiFase2 = 48;
        break;
      case '3+2':
        mesiFase1 = 36;
        mesiFase2 = 24;
        break;
      case '4+2':
        mesiFase1 = 48;
        mesiFase2 = 24;
        break;
      case 'transitorio':
        mesiFase1 = 18;
        mesiFase2 = 0;
        break;
      default:
        // Per "altro", usa data_fine se presente
        if (contratto.data_fine) {
          return {
            fase1: {
              inizio: dataInizio,
              fine: new Date(contratto.data_fine)
            },
            fase2: null,
            tipo: tipoContratto
          };
        }
        return null;
    }

    // Calcola fine fase 1 (giorno precedente alla data di inizio + mesi)
    const fineFase1 = new Date(dataInizio);
    fineFase1.setMonth(fineFase1.getMonth() + mesiFase1);
    fineFase1.setDate(fineFase1.getDate() - 1);

    // Calcola inizio e fine fase 2
    let fase2 = null;
    if (mesiFase2 > 0) {
      const inizioFase2 = new Date(fineFase1);
      inizioFase2.setDate(inizioFase2.getDate() + 1);
      
      const fineFase2 = new Date(inizioFase2);
      fineFase2.setMonth(fineFase2.getMonth() + mesiFase2);
      fineFase2.setDate(fineFase2.getDate() - 1);
      
      fase2 = {
        inizio: inizioFase2,
        fine: fineFase2
      };
    }

    return {
      fase1: {
        inizio: dataInizio,
        fine: fineFase1
      },
      fase2: fase2,
      tipo: tipoContratto
    };
  };

  // Calcola date contratto (prima scadenza e fine) da data inizio e tipo
  const calcolaDateContratto = (dataInizio, tipoContratto) => {
    if (!dataInizio || !tipoContratto) return null;
    
    let mesiFase1 = 0;
    let mesiFase2 = 0;
    
    switch (tipoContratto) {
      case '4+4':
        mesiFase1 = 48;
        mesiFase2 = 48;
        break;
      case '3+2':
        mesiFase1 = 36;
        mesiFase2 = 24;
        break;
      case '4+2':
        mesiFase1 = 48;
        mesiFase2 = 24;
        break;
      case 'transitorio':
        mesiFase1 = 18;
        mesiFase2 = 0;
        break;
      default:
        return null; // Per "altro" non calcola automaticamente
    }
    
    // Calcolo in UTC: evita lo slittamento di un giorno col cambio ora legale
    const [y, m, d] = String(dataInizio).slice(0, 10).split('-').map(Number);
    const primaScadenza = new Date(Date.UTC(y, m - 1 + mesiFase1, d - 1));
    const fineContratto = new Date(Date.UTC(y, m - 1 + mesiFase1 + mesiFase2, d - 1));
    
    // Formatta date in YYYY-MM-DD per input type="date"
    const formatDate = (d) => d.toISOString().split('T')[0];
    
    return {
      prima_scadenza: formatDate(primaScadenza),
      data_fine: formatDate(fineContratto)
    };
  };

  // Formatta una data in formato italiano
  const formatDataIT = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('it-IT');
  };

  const fetchData = useCallback(async (silent = false) => {
    try {
      const [contrattiRes, immRes, usersRes] = await Promise.all([
        contrattiApi.getAll(),
        immobiliApi.getAll(),
        usersApi.getAll()
      ]);
      setContratti(contrattiRes.data);
      setImmobili(immRes.data);
      setUsers(usersRes.data.filter(u => u.ruolo !== 'admin'));
      // NON sovrascrivere selectedContratto durante un refresh silent (post-save):
      // l'utente potrebbe stare digitando in altri campi del Dialog Documenti.
      const current = selectedContrattoRef.current;
      if (!silent && current) {
        const updated = contrattiRes.data.find(c => c.id === current.id);
        if (updated) setSelectedContratto(updated);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('fetchData error:', error?.response?.status, error?.config?.url, error?.response?.data);
      if (!silent) {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        toast.error(`Errore caricamento dati${status ? ` (${status})` : ''}${detail && typeof detail === 'string' ? `: ${detail}` : ''}`);
      }
    } finally {
      setLoading(false);
    }
  }, []);  // ← deps vuote: usa selectedContrattoRef per accedere a selectedContratto senza causare ricreazioni del callback

  useEffect(() => {
    fetchData();
    fetchTipiDocumento();
  }, [fetchData]);

  const fetchTipiDocumento = async () => {
    try {
      const res = await tipiDocumentoApi.getAll();
      setTipiDocumento(res.data);
    } catch (error) {
      console.error('Error fetching tipi documento:', error);
    }
  };

  // Crea nuovo inquilino inline
  const handleCreateInquilino = async () => {
    try {
      if (!newInquilinoData.nome || !newInquilinoData.cognome) {
        toast.error('Nome e Cognome sono obbligatori');
        return;
      }

      const response = await usersApi.create(newInquilinoData);
      const newUser = response.data;
      
      // Aggiorna lista utenti
      setUsers([...users, newUser]);
      
      // Seleziona automaticamente il nuovo inquilino
      handleInquiliniChange([...formData.inquilini_ids, newUser.id]);
      
      // Reset e chiudi dialog
      setNewInquilinoData({ 
        nome: '', cognome: '', email: '', codice_fiscale: '', iban: '',
        telefono: '', pec: '', altro_contatto: '', data_nascita: '',
        luogo_nascita: '', provincia_nascita: '', indirizzo_residenza: '',
        cap_residenza: '', citta_residenza: '', provincia_residenza: ''
      });
      setShowNewInquilinoDialog(false);
      
      toast.success(`Inquilino "${newInquilinoData.nome} ${newInquilinoData.cognome}" creato e selezionato`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  // Crea nuovo garante inline
  const handleCreateGarante = async () => {
    try {
      if (!newGaranteData.nome || !newGaranteData.cognome) {
        toast.error('Nome e Cognome sono obbligatori');
        return;
      }

      const response = await usersApi.create(newGaranteData);
      const newUser = response.data;
      
      // Aggiorna lista utenti
      setUsers([...users, newUser]);
      
      // Seleziona automaticamente il nuovo garante
      setFormData(prev => ({
        ...prev,
        garanti_ids: [...(prev.garanti_ids || []), newUser.id]
      }));
      
      // Reset e chiudi dialog
      setNewGaranteData({ 
        nome: '', cognome: '', email: '', codice_fiscale: '', iban: '',
        telefono: '', pec: '', altro_contatto: '', data_nascita: '',
        luogo_nascita: '', provincia_nascita: '', indirizzo_residenza: '',
        cap_residenza: '', citta_residenza: '', provincia_residenza: ''
      });
      setShowNewGaranteDialog(false);
      
      toast.success(`Garante "${newGaranteData.nome} ${newGaranteData.cognome}" creato e selezionato`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  // Ricerca comuni per form nuovo utente (residenza)
  const searchComuniNewUser = async (query) => {
    if (!query || query.length < 2) {
      setNewUserComuniSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/comuni?q=${encodeURIComponent(query)}`);
      setNewUserComuniSuggestions(response.data || []);
    } catch (err) {
      console.error('Errore ricerca comuni:', err);
    }
  };

  // Ricerca comuni per form nuovo utente (nascita)
  const searchComuniNascitaNewUser = async (query) => {
    if (!query || query.length < 2) {
      setNewUserComuniNascitaSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/comuni?q=${encodeURIComponent(query)}`);
      setNewUserComuniNascitaSuggestions(response.data || []);
    } catch (err) {
      console.error('Errore ricerca comuni:', err);
    }
  };

  // Ricerca comuni per autocomplete
  const searchComuni = async (query) => {
    if (!query || query.length < 2) {
      setComuniSuggestions([]);
      return;
    }
    setSearchingComuni(true);
    try {
      const response = await axios.get(`${API_URL}/comuni?q=${encodeURIComponent(query)}`);
      const mapped = response.data.map(c => ({
        nome: c.nome,
        provincia: c.provincia,
        cap: c.cap,
        codice_catastale: c.codice_catastale,
        label: `${c.nome} (${c.provincia}) - ${c.cap}`
      }));
      setComuniSuggestions(mapped.slice(0, 10));
    } catch (error) {
      console.error('Errore ricerca comuni:', error);
    }
    setSearchingComuni(false);
  };

  // Seleziona comune da autocomplete
  const handleSelectComune = (comune) => {
    setNewImmobileData({
      ...newImmobileData,
      citta: comune.nome,
      cap: comune.cap,
      provincia: comune.provincia
    });
    setComuniSuggestions([]);
  };

  // Crea nuovo immobile inline
  const handleCreateImmobile = async () => {
    try {
      if (!newImmobileData.indirizzo || !newImmobileData.citta || !newImmobileData.cap || !newImmobileData.provincia) {
        toast.error('Indirizzo, Città, CAP e Provincia sono obbligatori');
        return;
      }
      if (newImmobileData.proprietari_ids.length === 0) {
        toast.error('Seleziona almeno un proprietario');
        return;
      }

      const response = await immobiliApi.create(newImmobileData);
      const newImmobile = response.data;
      
      // Aggiorna lista immobili
      setImmobili([...immobili, newImmobile]);
      
      // Seleziona automaticamente il nuovo immobile
      handleImmobileChange(newImmobile.id);
      
      // Reset e chiudi dialog
      setNewImmobileData({ indirizzo: '', citta: '', cap: '', provincia: '', proprietari_ids: [] });
      setComuniSuggestions([]);
      setShowNewImmobileDialog(false);
      
      toast.success(`Immobile "${newImmobileData.indirizzo}, ${newImmobileData.citta}" creato e selezionato`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  // When immobile changes, update proprietari list
  const handleImmobileChange = (propertyId) => {
    setFormData(prev => ({ ...prev, property_id: propertyId, iban_bonifico_id: '' }));
    const imm = immobili.find(i => i.id === propertyId);
    if (imm) {
      const propIds = imm.proprietari_ids || (imm.proprietario_id ? [imm.proprietario_id] : []);
      const proprietari = users.filter(u => propIds.includes(u.id));
      setSelectedImmobileProprietari(proprietari);
      // Auto-select first proprietario for IBAN if only one
      if (proprietari.length === 1) {
        setFormData(prev => ({ ...prev, iban_bonifico_id: proprietari[0].id }));
      }
    } else {
      setSelectedImmobileProprietari([]);
    }
  };

  // When inquilini change, reset IBAN addebito if needed
  const handleInquiliniChange = (ids) => {
    setFormData(prev => {
      const newData = { ...prev, inquilini_ids: ids };
      // If only one inquilino and no garanti, auto-select for SDD
      if (ids.length === 1 && (!prev.garanti_ids || prev.garanti_ids.length === 0)) {
        newData.iban_addebito_id = ids[0];
      } else if (!ids.includes(prev.iban_addebito_id) && !(prev.garanti_ids || []).includes(prev.iban_addebito_id)) {
        // Reset only if current selection is not in inquilini AND not in garanti
        newData.iban_addebito_id = '';
      }
      // Reset intestatario fattura se il conduttore selezionato non è più in lista
      if (prev.intestatario_fattura_id && !ids.includes(prev.intestatario_fattura_id)) {
        newData.intestatario_fattura_id = '';
      }
      return newData;
    });
  };

  const handleSubmit = async (e, statoOverride = null) => {
    e.preventDefault();
    
    // Usa lo stato passato come parametro o quello del formData
    const statoFinale = statoOverride || formData.stato;
    const isBozza = statoFinale === 'in_compilazione';
    
    if (!isBozza) {
      // Validazione completa solo per contratti attivi
      if (formData.inquilini_ids.length === 0) {
        toast.error('Seleziona almeno un inquilino');
        return;
      }
      if (!formData.iban_bonifico_id) {
        toast.error('Seleziona il proprietario per il bonifico SEPA');
        return;
      }
      if (!formData.iban_addebito_id) {
        toast.error('Seleziona la persona per l\'addebito SDD (inquilino o garante)');
        return;
      }
      if (!formData.data_inizio || !formData.data_fine) {
        toast.error('Inserisci le date di inizio e fine contratto');
        return;
      }
      if (!formData.canone_mensile || parseFloat(formData.canone_mensile) <= 0) {
        toast.error('Inserisci il canone mensile');
        return;
      }
    }

    try {
      const data = {
        ...formData,
        data_fine: calcolaDateContratto(formData.data_inizio, formData.tipo_contratto)?.data_fine || formData.data_fine,
        stato: statoFinale,
        canone_mensile: parseFloat(formData.canone_mensile) || 0,
        spese_condominiali: parseFloat(formData.spese_condominiali) || 0,
        spese_forfettarie: parseFloat(formData.spese_forfettarie) || 0,
        quota_semplificaffitto_proprietario_perc: parseFloat(formData.quota_semplificaffitto_proprietario_perc) || 0,
        quota_semplificaffitto_inquilino_perc: parseFloat(formData.quota_semplificaffitto_inquilino_perc) || 0,
        sconto_sa_proprietario: parseFloat(formData.sconto_sa_proprietario || 0),
        sconto_sa_inquilino: parseFloat(formData.sconto_sa_inquilino || 0),
        sconto_sa_inquilino_fino_a: formData.sconto_sa_inquilino_fino_a ? parseInt(formData.sconto_sa_inquilino_fino_a) : null,
        quota_bollo: parseFloat(formData.quota_bollo) || 0,
        mesi_indennizzo: parseInt(formData.mesi_indennizzo) || 3,
        prosecuzione_contratto_id: formData.prosecuzione_contratto_id || null,
      };

      if (editingItem) {
        // Don't allow changing property_id in edit mode
        const { property_id, ...updateData } = data;
        await contrattiApi.update(editingItem.id, updateData);
        toast.success(isBozza ? 'Bozza salvata' : 'Contratto aggiornato e attivato');
      } else {
        await contrattiApi.create(data);
        toast.success(isBozza ? 'Bozza salvata' : 'Contratto creato e attivato');
      }
      
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  // Funzione wrapper per passare lo stato
  const handleSubmitWithStato = (stato) => {
    handleSubmit({ preventDefault: () => {} }, stato);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setDatiEconomiciSbloccati(false); // Reset blocco dati economici
    const inquiliniIds = item.inquilini_ids || (item.inquilino_id ? [item.inquilino_id] : []);
    
    // Get proprietari for this contract's property
    const imm = immobili.find(i => i.id === item.property_id);
    if (imm) {
      const propIds = imm.proprietari_ids || (imm.proprietario_id ? [imm.proprietario_id] : []);
      const proprietari = users.filter(u => propIds.includes(u.id));
      setSelectedImmobileProprietari(proprietari);
    }

    setFormData({
      property_id: item.property_id,
      inquilini_ids: inquiliniIds,
      garanti_ids: item.garanti_ids || [],
      iban_bonifico_id: item.iban_bonifico_id || '',
      iban_addebito_id: item.iban_addebito_id || inquiliniIds[0] || '',
      intestatario_fattura_id: item.intestatario_fattura_id || '',
      data_inizio: item.data_inizio || '',
      data_fine: item.data_fine || '',
      tipo_contratto: item.tipo_contratto || '4+4',
      regime_fiscale: item.regime_fiscale || 'cedolare',
      canone_mensile: item.canone_mensile?.toString() || '',
      spese_condominiali: item.spese_condominiali?.toString() || '0',
      spese_forfettarie: item.spese_forfettarie?.toString() || '0',
      quota_semplificaffitto_proprietario_perc: item.quota_semplificaffitto_proprietario_perc?.toString() || '7',
      quota_semplificaffitto_inquilino_perc: item.quota_semplificaffitto_inquilino_perc?.toString() || '9',
      sconto_sa_proprietario: item.sconto_sa_proprietario?.toString() || '0',
      sconto_sa_inquilino: item.sconto_sa_inquilino?.toString() || '0',
      sconto_sa_inquilino_fino_a: item.sconto_sa_inquilino_fino_a?.toString() || '',
      quota_bollo: item.quota_bollo?.toString() || '2.00',
      mesi_indennizzo: item.mesi_indennizzo?.toString() || '6',
      note: item.note || '',
      prosecuzione_contratto_id: item.prosecuzione_contratto_id || '',
      stato: item.stato || 'attivo',
      // Codici SDD
      mandato_sdd_codice: item.mandato_sdd_codice || '',
      mandato_sdd_data_firma: item.mandato_sdd_data_firma || '',
      contratto_servizio_codice: item.contratto_servizio_codice || '',
      data_firma_contratto_servizio: item.data_firma_contratto_servizio || '',
      // Codici documenti di servizio (override manuale)
      mandato_servizio_codice: item.mandato_servizio_codice || '',
      mandato_sdd_locatore_codice: item.mandato_sdd_locatore_codice || '',
      mandato_sdd_conduttore_codice: item.mandato_sdd_conduttore_codice || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo contratto?')) return;
    
    try {
      await contrattiApi.delete(id);
      toast.success('Contratto eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  // Apre il dialog per cessare contratto con data
  const openCessaDialog = (id) => {
    setCessaContrattoId(id);
    // Imposta data default a fine mese corrente
    const oggi = new Date();
    const fineMesseCorrente = new Date(oggi.getFullYear(), oggi.getMonth() + 1, 0);
    setDataCessazione(fineMesseCorrente.toISOString().split('T')[0]);
    setMotivoCessazione('');
    setShowCessaDialog(true);
  };

  const handleCessaContratto = async () => {
    if (!dataCessazione) {
      toast.error('Inserisci una data di cessazione');
      return;
    }
    
    try {
      const response = await contractsApi.cessa(cessaContrattoId, {
        data_cessazione: dataCessazione,
        motivo: motivoCessazione || null
      });
      
      toast.success(
        `Contratto cessato dal ${dataCessazione}. ` +
        `${response.data.pagamenti_ordinari_cancellati > 0 
          ? `Cancellati ${response.data.pagamenti_ordinari_cancellati} pagamenti ordinari futuri.` 
          : ''}`
      );
      
      setShowCessaDialog(false);
      setCessaContrattoId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la cessazione');
    }
  };

  const handleRiattivaContratto = async (id) => {
    if (!window.confirm('Riattivare questo contratto? Verranno ripresi i pagamenti automatici.')) return;
    
    try {
      await contrattiApi.update(id, { stato: 'attivo' });
      toast.success('Contratto riattivato con successo');
      fetchData();
    } catch (error) {
      toast.error('Errore durante la riattivazione');
    }
  };

  const handleAttivaContratto = async (id) => {
    if (!window.confirm('Attivare questo contratto? Verranno generati i pagamenti automatici.')) return;
    
    try {
      await contrattiApi.update(id, { stato: 'attivo' });
      toast.success('Contratto attivato con successo');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'attivazione');
    }
  };

  const handleOpenDocumenti = async (contratto) => {
    setSelectedContratto(contratto);
    try {
      const [docsRes, mandatoRes, contrattoRes] = await Promise.all([
        contrattiApi.getDocumenti(contratto.id),
        contrattiApi.getMandatoServizio(contratto.id),
        contrattiApi.getContrattoServizio(contratto.id)
      ]);
      setDocumentiContratto(docsRes.data);
      setMandatoServizio(mandatoRes.data);
      setContrattoServizio(contrattoRes.data);
    } catch (error) {
      setDocumentiContratto([]);
      setMandatoServizio(null);
      setContrattoServizio(null);
    }
    setUploadDocData({ tipo_documento: '', file: null, note: '' });
    // Inizializza dati registrazione dal contratto
    const reg = contratto.registrazione_contratto || {};
    setRegData({
      ufficio_territoriale: reg.ufficio_territoriale || '',
      data_registrazione: reg.data_registrazione || '',
      protocollo: reg.protocollo || '',
      codice_identificativo: reg.codice_identificativo || ''
    });
    setShowDocumentiDialog(true);
  };

  const handleGeneraMandato = async () => {
    try {
      const res = await contrattiApi.generaMandatoServizio(selectedContratto.id);
      if (res.data.already_exists) {
        toast.info(`Mandato già esistente: ${res.data.codice}`);
      } else {
        toast.success(`Mandato generato: ${res.data.codice}`);
      }
      setMandatoServizio(res.data);
      fetchData();
    } catch (error) {
      toast.error('Errore nella generazione');
    }
  };

  const handleGeneraContrattoServizio = async () => {
    try {
      const res = await contrattiApi.generaContrattoServizio(selectedContratto.id);
      if (res.data.already_exists) {
        toast.info(`Contratto servizio già esistente: ${res.data.codice}`);
      } else {
        toast.success(`Contratto servizio generato: ${res.data.codice}`);
      }
      setContrattoServizio(res.data);
      fetchData();
    } catch (error) {
      toast.error('Errore nella generazione');
    }
  };

  // Download DOCX con autenticazione (cookie httpOnly)
  const handleDownloadMandatoDocx = async () => {
    try {
      const response = await fetch(contractsApi.downloadMandatoDocx(selectedContratto.id), {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mandato_${mandatoServizio?.codice || 'documento'}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download completato');
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const handleDownloadContrattoDocx = async () => {
    try {
      const response = await fetch(contractsApi.downloadContrattoServizioDocx(selectedContratto.id), {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ContrattoServizio_${contrattoServizio?.codice || 'documento'}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download completato');
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  // Helper: legge file come base64
  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // Helper: comprimi immagine via canvas
  const compressImage = (file, maxDim, quality) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > maxDim || h > maxDim) {
        const ratio = Math.min(maxDim / w, maxDim / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(dataUrl.split(',')[1]);
    };
    img.src = URL.createObjectURL(file);
  });

  const handleUploadDocumento = async () => {
    if (!uploadDocData.tipo_documento || !uploadDocData.file) {
      toast.error('Seleziona tipo documento e file');
      return;
    }

    try {
      const file = uploadDocData.file;
      const ext = file.name.includes('.') ? '.' + file.name.split('.').pop().toLowerCase() : '';
      const tipoLabel = tipiDocumento.find(t => t.value === uploadDocData.tipo_documento)?.label || uploadDocData.tipo_documento;
      const contractCode = selectedContratto?.codice_contratto || '';
      const renamedFilename = `${tipoLabel}${contractCode ? '_' + contractCode : ''}${ext}`.replace(/\s+/g, '_');

      // Comprimi immagini via canvas
      const isImage = file.type.startsWith('image/');
      let base64;
      if (isImage) {
        base64 = await compressImage(file, 1200, 0.7);
      } else {
        const raw = await readFileAsBase64(file);
        base64 = raw.split(',')[1];
      }

      const payload = {
        tipo_documento: uploadDocData.tipo_documento,
        filename: renamedFilename,
        file_data: base64,
        note: uploadDocData.note
      };
      await contrattiApi.aggiungiDocumento(selectedContratto.id, payload);
      toast.success(`Documento "${renamedFilename}" caricato`);
      const docsRes = await contrattiApi.getDocumenti(selectedContratto.id);
      setDocumentiContratto(docsRes.data);
      fetchData();
      setUploadDocData({ tipo_documento: '', file: null, note: '', ufficio_territoriale: '', data_registrazione: '', protocollo: '', codice_identificativo: '' });
    } catch (error) {
      toast.error('Errore nel caricamento');
    }
  };

  const handleDownloadDocumento = async (docId, filename) => {
    try {
      const res = await contrattiApi.downloadDocumento(selectedContratto.id, docId);
      let blob = res.data;
      // Legacy: risposta JSON con file_data base64 (retrocompatibilità)
      if (blob && blob.type && blob.type.includes('application/json')) {
        const text = await blob.text();
        const data = JSON.parse(text);
        if (!data.file_data) {
          toast.error('File non disponibile');
          return;
        }
        const byteChars = atob(data.file_data);
        const arr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) arr[i] = byteChars.charCodeAt(i);
        blob = new Blob([arr]);
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const handleDeleteDocumento = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await contrattiApi.eliminaDocumento(selectedContratto.id, docId);
      toast.success('Documento eliminato');
      const docsRes = await contrattiApi.getDocumenti(selectedContratto.id);
      setDocumentiContratto(docsRes.data);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleOpenSubentro = (contratto) => {
    setSelectedContratto(contratto);
    const inquiliniIds = contratto.inquilini_ids || (contratto.inquilino_id ? [contratto.inquilino_id] : []);
    setSubentroData({
      vecchio_inquilino_id: inquiliniIds[0] || '',
      nuovo_inquilino_id: '',
      data_subentro: new Date().toISOString().split('T')[0],
      note: ''
    });
    setShowSubentroDialog(true);
  };

  const handleSubentro = async () => {
    if (!subentroData.nuovo_inquilino_id || !subentroData.data_subentro) {
      toast.error('Compila tutti i campi richiesti');
      return;
    }
    try {
      await contrattiApi.registraSubentro(selectedContratto.id, subentroData);
      toast.success('Subentro registrato con successo');
      setShowSubentroDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il subentro');
    }
  };

  // Storico Pagamenti
  const handleOpenStoricoPagamenti = async (contratto) => {
    setSelectedContratto(contratto);
    try {
      const response = await axios.get(`${API_URL}/contracts/${contratto.id}/pagamenti-storico`);
      setStoricoPagamentiData(response.data);
      // Espandi l'ultima annualità per default (quella corrente)
      const lastAnn = response.data.annualita?.[response.data.annualita.length - 1];
      if (lastAnn) {
        setExpandedAnnualita({ [lastAnn.numero]: true });
      }
      setShowStoricoPagamentiDialog(true);
    } catch (error) {
      toast.error('Errore caricamento storico pagamenti');
    }
  };

  // Invio massivo fatture unite (per lato conduttore/locatore) via email
  const [invioFattureLoading, setInvioFattureLoading] = useState(null); // 'conduttore' | 'locatore' | null
  const handleInviaFattureUnite = async (tipo) => {
    if (!selectedContratto) return;
    setInvioFattureLoading(tipo);
    try {
      const res = await axios.post(`${API_URL}/contracts/${selectedContratto.id}/invia-fatture-unite`, { tipo });
      toast.success(res.data?.message || 'Richiesta presa in carico. Riceverai le fatture via email.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore avvio invio fatture');
    } finally {
      setInvioFattureLoading(null);
    }
  };


  const handleUploadFattura = async (pagamentoId, file, tipoFattura) => {
    if (!file || !pagamentoId) {
      toast.error('Pagamento non presente nel DB per questo mese');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setUploadingFattura(pagamentoId);
      await axios.post(`${API_URL}/payments/${pagamentoId}/fattura-manuale`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Fattura ${tipoFattura} caricata con successo`);
      // Ricarica storico
      const response = await axios.get(`${API_URL}/contracts/${selectedContratto.id}/pagamenti-storico`);
      setStoricoPagamentiData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore upload fattura');
    } finally {
      setUploadingFattura(null);
    }
  };

  const handleUploadFatturaMese = async (rata, tipoFattura, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('month', rata.month);
    formData.append('rata_numero', rata.rata_numero);
    formData.append('tipo', tipoFattura); // 'conduttore' | 'locatore'
    if (rata.sdd_amount != null) formData.append('sdd_amount', rata.sdd_amount);
    if (rata.sepa_amount != null) formData.append('sepa_amount', rata.sepa_amount);
    try {
      await axios.post(`${API_URL}/contracts/${selectedContratto.id}/carica-fattura-mese`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Fattura ${tipoFattura} caricata (rata ${rata.rata_numero} creata)`);
      const response = await axios.get(`${API_URL}/contracts/${selectedContratto.id}/pagamenti-storico`);
      setStoricoPagamentiData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore upload fattura');
    }
  };

  const handleImportaFatturaFic = async (pagamentoId) => {
    if (!pagamentoId) return;
    try {
      const r = await axios.post(`${API_URL}/payments/${pagamentoId}/importa-fattura-fic`);
      toast.success(`Fattura importata da FIC${r.data.ei_status ? ' — SDI: ' + r.data.ei_status : ''}`);
      const response = await axios.get(`${API_URL}/contracts/${selectedContratto.id}/pagamenti-storico`);
      setStoricoPagamentiData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore importazione da FIC');
    }
  };

  const handleDeleteFatturaStorico = async (pagamentoId, tipoFattura, numero) => {
    if (!pagamentoId) return;
    if (!window.confirm(`Cancellare la fattura ${tipoFattura}${numero ? ' ' + numero : ''} da questa rata?`)) return;
    try {
      await axios.delete(`${API_URL}/payments/${pagamentoId}/fattura`);
      toast.success('Fattura rimossa dalla rata');
      const response = await axios.get(`${API_URL}/contracts/${selectedContratto.id}/pagamenti-storico`);
      setStoricoPagamentiData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore cancellazione fattura');
    }
  };

  const handleDownloadFattura = async (pagamentoId, tipoFattura) => {
    if (!pagamentoId) return;
    try {
      const response = await axios.get(`${API_URL}/payments/${pagamentoId}/fattura-manuale/download`, {
        responseType: 'blob'
      });
      const disposition = response.headers['content-disposition'];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', disposition?.match(/filename=(.+)/)?.[1] || `fattura_${tipoFattura}_${pagamentoId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Errore download fattura');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setSelectedImmobileProprietari([]);
    setFormData({
      property_id: '',
      inquilini_ids: [],
      garanti_ids: [],
      iban_bonifico_id: '',
      iban_addebito_id: '',
      intestatario_fattura_id: '',
      data_inizio: '',
      data_fine: '',
      tipo_contratto: '4+4',
      regime_fiscale: 'cedolare',
      canone_mensile: '',
      spese_condominiali: '0',
      spese_forfettarie: '0',
      quota_semplificaffitto_proprietario_perc: '7',
      quota_semplificaffitto_inquilino_perc: '9',
      sconto_sa_proprietario: '0',
      sconto_sa_inquilino: '0',
      sconto_sa_inquilino_fino_a: '',
      quota_bollo: '2.00',
      mesi_indennizzo: '3',
      note: '',
      prosecuzione_contratto_id: '',
    });
  };

  const getImmobileInfo = (id) => {
    const imm = immobili.find(i => i.id === id);
    return imm ? `${imm.indirizzo}, ${imm.citta}` : '-';
  };

  const getUserInfo = useCallback((id) => {
    const user = users.find(u => u.id === id);
    if (!user) return '-';
    // Se è una società, mostra ragione sociale
    if (user.is_societa && user.ragione_sociale) {
      return user.ragione_sociale;
    }
    // Cognome Nome
    return `${user.cognome || ''} ${user.nome || ''}`.trim() || '-';
  }, [users]);

  const getInquiliniInfo = useCallback((item) => {
    const ids = item.inquilini_ids || (item.inquilino_id ? [item.inquilino_id] : []);
    return ids.map(id => getUserInfo(id)).join(', ');
  }, [getUserInfo]);

  const getProprietariInfo = useCallback((propertyId) => {
    const imm = immobili.find(i => i.id === propertyId);
    if (!imm) return '-';
    const propIds = imm.proprietari_ids || (imm.proprietario_id ? [imm.proprietario_id] : []);
    if (propIds.length === 0) return '-';
    return propIds.map(id => getUserInfo(id)).join(', ');
  }, [immobili, getUserInfo]);

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'in_compilazione': return 'bg-blue-100 text-blue-700';
      case 'attivo': return 'bg-green-100 text-green-700';
      case 'cessato': return 'bg-orange-100 text-orange-700';
      case 'scaduto': return 'bg-yellow-100 text-yellow-700';
      case 'terminato': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Prepare options for comboboxes (Cognome Nome)
  const userOptions = users.map(u => ({
    value: u.id,
    label: u.is_societa && u.ragione_sociale ? u.ragione_sociale : `${u.cognome || ''} ${u.nome || ''}`.trim(),
    sublabel: u.iban ? `IBAN: ${u.iban.substring(0, 12)}...` : 'IBAN non presente'
  }));

  const immobileOptions = immobili.map(i => ({
    value: i.id,
    label: `${i.indirizzo}, ${i.citta}`
  }));

  const proprietariOptions = selectedImmobileProprietari.map(p => ({
    value: p.id,
    label: p.is_societa && p.ragione_sociale ? p.ragione_sociale : `${p.cognome || ''} ${p.nome || ''}`.trim(),
    sublabel: p.iban ? `IBAN: ${p.iban.substring(0, 12)}...` : 'IBAN mancante!'
  }));

  const inquiliniSelezionatiOptions = formData.inquilini_ids.map(id => {
    const user = users.find(u => u.id === id);
    return user ? {
      value: user.id,
      label: user.is_societa && user.ragione_sociale ? user.ragione_sociale : `${user.nome || ''} ${user.cognome || ''}`.trim(),
      sublabel: user.iban ? `IBAN: ${user.iban.substring(0, 12)}...` : 'IBAN mancante!'
    } : null;
  }).filter(Boolean);

  // Opzioni IBAN per Addebito: include inquilini + garanti
  const ibanAddebitoOptions = [
    ...formData.inquilini_ids.map(id => {
      const user = users.find(u => u.id === id);
      return user ? {
        value: user.id,
        label: `${user.is_societa && user.ragione_sociale ? user.ragione_sociale : `${user.nome || ''} ${user.cognome || ''}`.trim()} (Inquilino)`,
        sublabel: user.iban ? `IBAN: ${user.iban.substring(0, 12)}...` : 'IBAN mancante!'
      } : null;
    }).filter(Boolean),
    ...(formData.garanti_ids || []).map(id => {
      const user = users.find(u => u.id === id);
      return user ? {
        value: user.id,
        label: `${user.is_societa && user.ragione_sociale ? user.ragione_sociale : `${user.nome || ''} ${user.cognome || ''}`.trim()} (Garante)`,
        sublabel: user.iban ? `IBAN: ${user.iban.substring(0, 12)}...` : 'IBAN mancante!'
      } : null;
    }).filter(Boolean)
  ];

  // Ricerca migliorata: immobile, inquilino, proprietario, codice
  const filteredContratti = contratti.filter(c => {
    const searchLower = searchTerm.toLowerCase();
    
    // Cerca in immobile (indirizzo + città)
    const matchImmobile = getImmobileInfo(c.property_id).toLowerCase().includes(searchLower);
    
    // Cerca in inquilini
    const matchInquilino = getInquiliniInfo(c).toLowerCase().includes(searchLower);
    
    // Cerca in proprietari
    const matchProprietario = getProprietariInfo(c.property_id).toLowerCase().includes(searchLower);
    
    // Cerca in codice contratto
    const matchCodice = (c.codice_contratto || '').toLowerCase().includes(searchLower);
    
    const matchSearch = !searchTerm || matchImmobile || matchInquilino || matchProprietario || matchCodice;
    // Stati: attivo, cessato (interrotto prima), terminato (scadenza naturale)
    const matchStato = filterStato === 'all' || c.stato === filterStato;
    return matchSearch && matchStato;
  });

  // Ordinamento colonne
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-40" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  // Ordinamento effettivo
  const sortedContratti = useMemo(() => {
    return [...filteredContratti].sort((a, b) => {
      let valA, valB;
      
      switch (sortColumn) {
        case 'codice_contratto':
          valA = a.codice_contratto || '';
          valB = b.codice_contratto || '';
          break;
        case 'immobile':
          const immA = immobili.find(i => i.id === a.property_id);
          const immB = immobili.find(i => i.id === b.property_id);
          valA = immA ? `${immA.citta} ${immA.indirizzo}` : '';
          valB = immB ? `${immB.citta} ${immB.indirizzo}` : '';
          break;
        case 'proprietario':
          valA = getProprietariInfo(a);
          valB = getProprietariInfo(b);
          break;
        case 'inquilino':
          valA = getInquiliniInfo(a);
          valB = getInquiliniInfo(b);
          break;
        case 'periodo':
          valA = a.data_inizio || '';
          valB = b.data_inizio || '';
          break;
        default:
          valA = a[sortColumn] || '';
          valB = b[sortColumn] || '';
      }
      
      if (sortDirection === 'asc') {
        return valA.localeCompare(valB);
      } else {
        return valB.localeCompare(valA);
      }
    });
  }, [filteredContratti, sortColumn, sortDirection, immobili, getInquiliniInfo, getProprietariInfo]);

  // Check if contract can manage payments (has mandato and contratto servizio)
  const canManagePayments = (contratto) => {
    return contratto.mandato_servizio_generato && contratto.contratto_servizio_generato;
  };

  // Sblocca dati economici
  const handleUnlockDatiEconomici = () => {
    setDatiEconomiciSbloccati(true);
    setShowUnlockConfirm(false);
    toast.info('Dati economici sbloccati. Le modifiche saranno tracciate.');
  };

  // Visualizza timeline modifiche
  const handleShowTimeline = async (contratto) => {
    setTimelineContratto(contratto.timeline_modifiche || []);
    setShowTimelineDialog(true);
  };

  // Export function
  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/export/contracts/${format}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contratti.${format === 'xlsx' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${format.toUpperCase()} completato`);
    } catch (error) {
      toast.error('Errore durante l\'export');
    }
  };

  // Calculate money values from percentages
  const calculateMoneyValue = (percentage, base) => {
    return ((parseFloat(percentage) || 0) * (parseFloat(base) || 0) / 100).toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="contratti-page">
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Contratti</h1>
            <p className="text-slate-500 mt-1">Gestisci i contratti di affitto</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleExport('xlsx')} data-testid="export-xlsx-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button variant="outline" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button 
              onClick={() => { resetForm(); setShowDialog(true); }}
              className="bg-slate-900 hover:bg-slate-800"
              disabled={immobili.length === 0 || users.length === 0}
              data-testid="new-contratto-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Contratto
            </Button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per immobile, inquilino, proprietario o codice..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-contratti"
                />
              </div>
              <Select value={filterStato} onValueChange={setFilterStato}>
                <SelectTrigger className="w-[180px]" data-testid="filter-stato">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="in_compilazione">In Compilazione</SelectItem>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="cessato">Cessato</SelectItem>
                  <SelectItem value="terminato">Terminato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0 max-h-[70vh] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10 shadow-sm">
                <TableRow>
                  <TableHead className="w-12 text-center">#</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50 select-none"
                    onClick={() => handleSort('codice_contratto')}
                  >
                    <div className="flex items-center">Codice {getSortIcon('codice_contratto')}</div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50 select-none"
                    onClick={() => handleSort('immobile')}
                  >
                    <div className="flex items-center">Immobile (Comune) {getSortIcon('immobile')}</div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50 select-none"
                    onClick={() => handleSort('proprietario')}
                  >
                    <div className="flex items-center">Proprietario/i {getSortIcon('proprietario')}</div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50 select-none"
                    onClick={() => handleSort('inquilino')}
                  >
                    <div className="flex items-center">Inquilino/i {getSortIcon('inquilino')}</div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50 select-none"
                    onClick={() => handleSort('periodo')}
                  >
                    <div className="flex items-center">Periodo {getSortIcon('periodo')}</div>
                  </TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedContratti.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      Nessun contratto trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedContratti.map((item, index) => (
                    <React.Fragment key={item.id}>
                    <TableRow data-testid={`contratto-row-${item.id}`}>
                      <TableCell className="text-center text-slate-400 font-mono text-sm">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        {item.codice_contratto ? (
                          <Badge className="bg-slate-100 text-slate-700 font-mono">
                            {item.codice_contratto}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">Da assegnare</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Home className="w-5 h-5 text-slate-600" />
                          </div>
                          <span className="font-medium">{getImmobileInfo(item.property_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-green-600" />
                          <div>
                            <span className="text-sm">{getProprietariInfo(item.property_id)}</span>
                            {/* Codice Mandato Servizio sotto il nominativo */}
                            <div className="mt-0.5">
                              {item.mandato_servizio_codice ? (
                                <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 font-mono">
                                  {item.mandato_servizio_codice}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-600 border-orange-300">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Mandato
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <div>
                            {getInquiliniInfo(item)}
                            {/* Garanti sotto il nome inquilino */}
                            {item.garanti_ids && item.garanti_ids.length > 0 && (
                              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                <span>Garante: {item.garanti_ids.map(id => getUserInfo(id)).join(', ')}</span>
                              </div>
                            )}
                            {/* Codice Contratto Servizio sotto il nominativo */}
                            <div className="mt-0.5">
                              {item.contratto_servizio_codice ? (
                                <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 font-mono">
                                  {item.contratto_servizio_codice}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-600 border-orange-300">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Contratto
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const fasi = calcolaFasiContratto(item);
                          if (fasi && fasi.fase2) {
                            // Contratto con due fasi (4+4, 3+2, 4+2)
                            return (
                              <div className="flex flex-col gap-1 text-xs">
                                <div className="flex items-center gap-1.5 text-slate-700">
                                  <Calendar className="w-3.5 h-3.5 text-blue-500" />
                                  <span className="font-medium">{formatDataIT(fasi.fase1.inizio)} - {formatDataIT(fasi.fase1.fine)}</span>
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">
                                    {fasi.tipo.split('+')[0]}A
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-1.5 text-slate-500">
                                  <Calendar className="w-3.5 h-3.5 text-green-500" />
                                  <span>{formatDataIT(fasi.fase2.inizio)} - {formatDataIT(fasi.fase2.fine)}</span>
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-green-50 text-green-700 border-green-200">
                                    +{fasi.tipo.split('+')[1]}A
                                  </Badge>
                                </div>
                              </div>
                            );
                          } else if (fasi && fasi.fase1) {
                            // Contratto con fase singola (transitorio o altro)
                            return (
                              <div className="flex items-center gap-1.5 text-xs text-slate-600">
                                <Calendar className="w-3.5 h-3.5 text-amber-500" />
                                <span className="font-medium">{formatDataIT(fasi.fase1.inizio)} - {formatDataIT(fasi.fase1.fine)}</span>
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">
                                  {fasi.tipo === 'transitorio' ? '18M' : fasi.tipo}
                                </Badge>
                              </div>
                            );
                          } else {
                            // Fallback se non c'è tipo o dati
                            return (
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Calendar className="w-4 h-4" />
                                {formatDataIT(item.data_inizio)} - {formatDataIT(item.data_fine)}
                              </div>
                            );
                          }
                        })()}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatoBadge(item.stato)}>
                          {item.stato.charAt(0).toUpperCase() + item.stato.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDocumenti(item)}
                            title="Documenti"
                            data-testid={`open-documenti-${item.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(item)}
                            data-testid={`edit-contratto-${item.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenStoricoPagamenti(item)}
                            title="Storico Pagamenti"
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <CreditCard className="w-4 h-4" />
                          </Button>
                          {item.stato === 'attivo' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenSubentro(item)}
                              title="Subentro Inquilino"
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          )}
                          {item.stato === 'in_compilazione' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAttivaContratto(item.id)}
                              className="text-green-600 hover:text-green-700"
                              title="Attiva Contratto"
                              data-testid={`attiva-contratto-${item.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {item.stato === 'attivo' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openCessaDialog(item.id)}
                              className="text-orange-600 hover:text-orange-700"
                              title="Cessa Contratto"
                              data-testid={`cessa-contratto-${item.id}`}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                          {item.stato === 'cessato' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRiattivaContratto(item.id)}
                              className="text-green-600 hover:text-green-700"
                              title="Riattiva Contratto"
                              data-testid={`riattiva-contratto-${item.id}`}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-contratto-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Striscia Registrazione Contratto */}
                    {item.registrazione_contratto && (
                      <TableRow className="bg-blue-50/60 hover:bg-blue-50/80" data-testid={`reg-strip-${item.id}`}>
                        <TableCell colSpan={8} className="py-1.5 px-4">
                          <div className="flex items-center gap-6 text-xs text-blue-700">
                            <div className="flex items-center gap-1.5 font-medium text-blue-800">
                              <FileText className="w-3 h-3" />
                              Registrazione:
                            </div>
                            {item.registrazione_contratto.ufficio_territoriale && (
                              <span>Ufficio: <strong>{item.registrazione_contratto.ufficio_territoriale}</strong></span>
                            )}
                            {item.registrazione_contratto.data_registrazione && (
                              <span>Data: <strong>{new Date(item.registrazione_contratto.data_registrazione).toLocaleDateString('it-IT')}</strong></span>
                            )}
                            {item.registrazione_contratto.protocollo && (
                              <span>Protocollo: <strong>{item.registrazione_contratto.protocollo}</strong></span>
                            )}
                            {item.registrazione_contratto.codice_identificativo && (
                              <span>Cod. Ident.: <strong>{item.registrazione_contratto.codice_identificativo}</strong></span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Dialog - Nuovo/Modifica Contratto */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Modifica Contratto' : 'Nuovo Contratto'}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Immobile */}
              <div className="form-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Home className="w-4 h-4" /> Immobile
                  </h3>
                  {!editingItem && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowNewImmobileDialog(true)}
                      data-testid="new-immobile-inline-btn"
                    >
                      <Building2 className="w-4 h-4 mr-1" />
                      Nuovo Immobile
                    </Button>
                  )}
                </div>
                {editingItem ? (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="font-medium">{getImmobileInfo(formData.property_id)}</p>
                    <p className="text-xs text-slate-500 mt-1">L'immobile non può essere modificato</p>
                  </div>
                ) : (
                  <Combobox
                    options={immobileOptions}
                    value={formData.property_id}
                    onValueChange={handleImmobileChange}
                    placeholder="Cerca e seleziona immobile..."
                    searchPlaceholder="Cerca indirizzo..."
                  />
                )}
              </div>

              {/* Proprietari (read-only, from immobile) */}
              {selectedImmobileProprietari.length > 0 && (
                <div className="form-section">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Proprietari (dall'immobile)
                  </h3>
                  <div className="bg-blue-50 p-3 rounded-lg space-y-2">
                    {selectedImmobileProprietari.map(p => (
                      <div key={p.id} className="flex items-center justify-between">
                        <span className="font-medium">
                          {p.is_societa && p.ragione_sociale 
                            ? p.ragione_sociale 
                            : `${p.nome || ''} ${p.cognome || ''}`.trim()}
                        </span>
                        {p.iban && <Badge variant="outline" className="text-xs">IBAN presente</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* IBAN per Bonifico SEPA */}
              {selectedImmobileProprietari.length > 0 && (
                <div className="form-section">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> IBAN Proprietario per Bonifico SEPA *
                  </h3>
                  <Combobox
                    options={proprietariOptions}
                    value={formData.iban_bonifico_id}
                    onValueChange={(v) => setFormData({...formData, iban_bonifico_id: v})}
                    placeholder="Seleziona proprietario..."
                    searchPlaceholder="Cerca..."
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Il bonifico SEPA sarà effettuato sull'IBAN di questo proprietario
                  </p>
                </div>
              )}

              {/* Inquilini */}
              <div className="form-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <User className="w-4 h-4" /> Inquilini *
                  </h3>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowNewInquilinoDialog(true)}
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Nuovo Inquilino
                  </Button>
                </div>
                <ComboboxMulti
                  options={userOptions}
                  values={formData.inquilini_ids}
                  onValuesChange={handleInquiliniChange}
                  placeholder="Cerca e seleziona inquilini..."
                  searchPlaceholder="Cerca per nome..."
                />
              </div>

              {/* IBAN per Addebito SDD */}
              {(formData.inquilini_ids.length > 0 || (formData.garanti_ids && formData.garanti_ids.length > 0)) && (
                <div className="form-section">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> IBAN per Addebito SDD *
                  </h3>
                  <Combobox
                    options={ibanAddebitoOptions}
                    value={formData.iban_addebito_id}
                    onValueChange={(v) => setFormData({...formData, iban_addebito_id: v})}
                    placeholder="Seleziona persona per addebito..."
                    searchPlaceholder="Cerca..."
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    L'addebito SDD sarà effettuato sull'IBAN selezionato (inquilino o garante)
                  </p>
                  {/* Cronologia modifiche al pagatore SDD (solo in editing) */}
                  {editingItem && <IbanHistoryPanel contractId={editingItem.id} />}
                </div>
              )}

              {/* Intestatario Fattura /CO — solo con più conduttori */}
              {formData.inquilini_ids.length > 1 && (
                <div className="form-section" data-testid="intestatario-fattura-section">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Intestatario Fattura (conduttore)
                  </h3>
                  <Combobox
                    options={inquiliniSelezionatiOptions}
                    value={formData.intestatario_fattura_id}
                    onValueChange={(v) => setFormData({...formData, intestatario_fattura_id: v})}
                    placeholder="Seleziona il conduttore a cui intestare la fattura..."
                    searchPlaceholder="Cerca..."
                    data-testid="intestatario-fattura-select"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Con più conduttori, scegli a chi intestare la fattura /CO. È indipendente da chi paga l'SDD.
                    Se non selezionato, la fattura va al pagatore SDD (se è un conduttore) o al primo conduttore.
                  </p>
                </div>
              )}

              {/* Garanti */}
              <div className="form-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Garanti (opzionale)</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNewGaranteDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Nuovo Garante
                  </Button>
                </div>
                <ComboboxMulti
                  options={userOptions.filter(u => !formData.inquilini_ids.includes(u.value))}
                  values={formData.garanti_ids}
                  onValuesChange={(v) => setFormData({...formData, garanti_ids: v})}
                  placeholder="Cerca e seleziona garanti..."
                  searchPlaceholder="Cerca per nome..."
                />
              </div>

              {/* Tipo Contratto - PRIMA della Durata */}
              <div className="form-section">
                <h3 className="font-semibold mb-3">Tipo Contratto</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo *</Label>
                    <Select 
                      value={formData.tipo_contratto} 
                      onValueChange={(v) => {
                        setFormData(prev => ({...prev, tipo_contratto: v}));
                        // Ricalcola date se data_inizio è già impostata
                        if (formData.data_inizio) {
                          const dates = calcolaDateContratto(formData.data_inizio, v);
                          if (dates) {
                            setFormData(prev => ({
                              ...prev, 
                              tipo_contratto: v,
                              data_fine: dates.data_fine
                            }));
                          }
                        }
                      }}
                      disabled={editingItem?.stato === 'attivo'}
                    >
                      <SelectTrigger className={editingItem?.stato === 'attivo' ? 'bg-slate-100' : ''}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4+4">4+4 (8 anni totali)</SelectItem>
                        <SelectItem value="3+2">3+2 (5 anni totali)</SelectItem>
                        <SelectItem value="4+2">4+2 (6 anni totali)</SelectItem>
                        <SelectItem value="transitorio">Transitorio (18 mesi)</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Regime Fiscale *</Label>
                    <Select 
                      value={formData.regime_fiscale} 
                      onValueChange={(v) => setFormData({...formData, regime_fiscale: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cedolare">Cedolare Secca</SelectItem>
                        <SelectItem value="ordinario">Ordinario</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Durata - con calcolo automatico */}
              <div className="form-section">
                <h3 className="font-semibold mb-3">Durata</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Data Inizio *</Label>
                    <Input
                      type="date"
                      value={formData.data_inizio}
                      onChange={(e) => {
                        const dataInizio = e.target.value;
                        const dates = calcolaDateContratto(dataInizio, formData.tipo_contratto);
                        if (dates) {
                          setFormData({
                            ...formData, 
                            data_inizio: dataInizio,
                            data_fine: dates.data_fine
                          });
                        } else {
                          setFormData({...formData, data_inizio: dataInizio});
                        }
                      }}
                      required
                      disabled={editingItem?.stato === 'attivo'}
                      className={editingItem?.stato === 'attivo' ? 'bg-slate-100' : ''}
                      data-testid="data-inizio-input"
                    />
                  </div>
                  <div>
                    <Label>Prima Scadenza</Label>
                    <Input
                      type="date"
                      value={formData.data_inizio && formData.tipo_contratto ? 
                        calcolaDateContratto(formData.data_inizio, formData.tipo_contratto)?.prima_scadenza || '' : ''}
                      disabled
                      className="bg-slate-50"
                    />
                    <p className="text-xs text-slate-500 mt-1">Calcolata automaticamente</p>
                  </div>
                  <div>
                    <Label>Fine Contratto</Label>
                    <Input
                      type="date"
                      value={calcolaDateContratto(formData.data_inizio, formData.tipo_contratto)?.data_fine || formData.data_fine}
                      disabled
                      className="bg-slate-50"
                    />
                    <p className="text-xs text-slate-500 mt-1">Calcolata automaticamente</p>
                  </div>
                </div>
              </div>

              {/* Prosecuzione contratto precedente */}
              <div className="form-section">
                <Label>Prosecuzione contratto precedente (opzionale)</Label>
                <Select value={formData.prosecuzione_contratto_id} onValueChange={(v) => setFormData({...formData, prosecuzione_contratto_id: v === '__none__' ? '' : v})}>
                  <SelectTrigger data-testid="prosecuzione-contratto-select">
                    <SelectValue placeholder="Nessuna prosecuzione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessuna prosecuzione</SelectItem>
                    {contratti.filter(c => c.id !== editingItem?.id).map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.codice_contratto} — {c.tipo_contratto} ({c.data_inizio?.slice(0,7)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.prosecuzione_contratto_id && (
                  <p className="text-xs text-blue-600 mt-1">La prima rata verrà splittata in 2 righe (prosecuzione + nuovo) nello storico pagamenti.</p>
                )}
              </div>

              {/* Dati Economici */}
              <div className="form-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Euro className="w-4 h-4" /> Dati Economici
                  </h3>
                  {editingItem && (
                    <div className="flex items-center gap-2">
                      {/* Pulsante Timeline */}
                      {editingItem.timeline_modifiche?.length > 0 && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleShowTimeline(editingItem)}
                        >
                          <History className="w-4 h-4 mr-1" />
                          Storico ({editingItem.timeline_modifiche.length})
                        </Button>
                      )}
                      {/* Pulsante Blocco/Sblocco */}
                      {!datiEconomiciSbloccati ? (
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowUnlockConfirm(true)}
                          className="text-amber-600 border-amber-300"
                        >
                          <Lock className="w-4 h-4 mr-1" />
                          Sblocca Modifica
                        </Button>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">
                          <Unlock className="w-3 h-3 mr-1" />
                          Sbloccato
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Avviso blocco per contratti esistenti */}
                {editingItem && !datiEconomiciSbloccati && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-700">
                    <Lock className="w-4 h-4 inline mr-2" />
                    I dati economici sono bloccati. Per modificarli (es. adeguamento ISTAT), clicca su "Sblocca Modifica".
                    Le modifiche saranno tracciate nello storico.
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Canone Mensile (€) *</Label>
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={formData.canone_mensile} 
                      onChange={(e) => setFormData({...formData, canone_mensile: e.target.value})} 
                      required 
                      disabled={editingItem && !datiEconomiciSbloccati}
                      className={editingItem && !datiEconomiciSbloccati ? 'bg-slate-100' : ''}
                    />
                  </div>
                  <div>
                    <Label>Spese Condominiali (€)</Label>
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={formData.spese_condominiali} 
                      onChange={(e) => setFormData({...formData, spese_condominiali: e.target.value})} 
                      disabled={editingItem && !datiEconomiciSbloccati}
                      className={editingItem && !datiEconomiciSbloccati ? 'bg-slate-100' : ''}
                    />
                  </div>
                  <div>
                    <Label>Spese Forfettarie (€)</Label>
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={formData.spese_forfettarie} 
                      onChange={(e) => setFormData({...formData, spese_forfettarie: e.target.value})} 
                      disabled={editingItem && !datiEconomiciSbloccati}
                      className={editingItem && !datiEconomiciSbloccati ? 'bg-slate-100' : ''}
                    />
                  </div>
                  
                  {/* Quota SA Proprietario con valore € calcolato */}
                  <div>
                    <Label>Quota SA Proprietario (%)</Label>
                    <div className="flex gap-2 items-center">
                      <Input 
                        type="number" 
                        step="0.01" 
                        value={formData.quota_semplificaffitto_proprietario_perc} 
                        onChange={(e) => setFormData({...formData, quota_semplificaffitto_proprietario_perc: e.target.value})} 
                        className={`flex-1 ${editingItem && !datiEconomiciSbloccati ? 'bg-slate-100' : ''}`}
                        disabled={editingItem && !datiEconomiciSbloccati}
                      />
                      <div className="bg-green-50 border border-green-200 rounded px-3 py-2 min-w-24 text-center">
                        <span className="text-green-700 font-semibold">
                          € {calculateMoneyValue(formData.quota_semplificaffitto_proprietario_perc, formData.canone_mensile)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Valore mensile calcolato sul canone</p>
                  </div>

                  {/* Sconto SA Proprietario */}
                  <div>
                    <Label>Sconto Incondizionato Proprietario (€)</Label>
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={formData.sconto_sa_proprietario} 
                      onChange={(e) => setFormData({...formData, sconto_sa_proprietario: e.target.value})} 
                      placeholder="0.00"
                      disabled={editingItem && !datiEconomiciSbloccati}
                      className={editingItem && !datiEconomiciSbloccati ? 'bg-slate-100' : ''}
                    />
                    <p className="text-xs text-slate-500 mt-1">Sconto che riduce la quota SA proprietario</p>
                  </div>
                  
                  {/* Quota SA Inquilino con valore € calcolato */}
                  <div>
                    <Label>Quota SA Inquilino (%)</Label>
                    <div className="flex gap-2 items-center">
                      <Input 
                        type="number" 
                        step="0.01" 
                        value={formData.quota_semplificaffitto_inquilino_perc} 
                        onChange={(e) => setFormData({...formData, quota_semplificaffitto_inquilino_perc: e.target.value})} 
                        className={`flex-1 ${editingItem && !datiEconomiciSbloccati ? 'bg-slate-100' : ''}`}
                        disabled={editingItem && !datiEconomiciSbloccati}
                      />
                      <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 min-w-24 text-center">
                        <span className="text-blue-700 font-semibold">
                          € {calculateMoneyValue(formData.quota_semplificaffitto_inquilino_perc, formData.canone_mensile)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Valore mensile calcolato sul canone</p>
                  </div>

                  {/* Sconto SA Inquilino */}
                  <div>
                    <Label>Sconto Incondizionato Inquilino (€)</Label>
                    <div className="flex gap-2">
                      <Input 
                        type="number" 
                        step="0.01" 
                        value={formData.sconto_sa_inquilino} 
                        onChange={(e) => setFormData({...formData, sconto_sa_inquilino: e.target.value})} 
                        placeholder="0.00"
                        disabled={editingItem && !datiEconomiciSbloccati}
                        className={`flex-1 ${editingItem && !datiEconomiciSbloccati ? 'bg-slate-100' : ''}`}
                      />
                      {parseFloat(formData.sconto_sa_inquilino || 0) > 0 && (
                        <Select
                          value={formData.sconto_sa_inquilino_fino_a}
                          onValueChange={(v) => setFormData({...formData, sconto_sa_inquilino_fino_a: v})}
                          disabled={editingItem && !datiEconomiciSbloccati}
                        >
                          <SelectTrigger className={`w-[160px] ${editingItem && !datiEconomiciSbloccati ? 'bg-slate-100' : ''}`} data-testid="sconto-fino-a-select">
                            <SelectValue placeholder="Fino a mens." />
                          </SelectTrigger>
                          <SelectContent>
                            {(() => {
                              const tipo = formData.tipo_contratto || '4+4';
                              let tot = 48;
                              try {
                                const parts = tipo.split('+');
                                tot = (parseInt(parts[0]) + (parts[1] ? parseInt(parts[1]) : 0)) * 12;
                              } catch (e) { console.warn('Parsing tipo_contratto:', e); }
                              if (formData.data_inizio && new Date(formData.data_inizio).getDate() > 1) tot += 1;
                              return Array.from({length: tot}, (_, i) => i + 1).map(n => (
                                <SelectItem key={n} value={n.toString()}>{n}/{tot}</SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Sconto che riduce la quota SA inquilino
                      {formData.sconto_sa_inquilino_fino_a && parseFloat(formData.sconto_sa_inquilino || 0) > 0 && (
                        <>
                          <span className="text-blue-600 font-medium"> — fino alla mensilità {formData.sconto_sa_inquilino_fino_a}</span>
                          {formData.data_inizio && (() => {
                            const giorno = parseInt(formData.data_inizio.slice(8, 10) || '1', 10);
                            if (giorno > 1) {
                              return (
                                <span className="block text-amber-600 text-[11px] mt-0.5">
                                  Contratto mid-month: la rata {parseInt(formData.sconto_sa_inquilino_fino_a) + 1} riceverà MEZZO sconto (completa il periodo)
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </>
                      )}
                    </p>
                  </div>
                  
                  <div>
                    <Label>Quota Bollo (€)</Label>
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={formData.quota_bollo} 
                      onChange={(e) => setFormData({...formData, quota_bollo: e.target.value})} 
                      disabled={editingItem && !datiEconomiciSbloccati}
                      className={editingItem && !datiEconomiciSbloccati ? 'bg-slate-100' : ''}
                    />
                  </div>

                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3" data-testid="mesi-indennizzo-field">
                    <Label className="text-yellow-800 font-semibold">Mesi di indennizzo operanti</Label>
                    <Input 
                      type="number" 
                      min="1" 
                      max="24" 
                      value={formData.mesi_indennizzo} 
                      onChange={(e) => setFormData({...formData, mesi_indennizzo: e.target.value})} 
                      className="bg-white border-yellow-400 font-bold text-lg mt-1"
                      data-testid="mesi-indennizzo-input"
                    />
                  </div>
                  
                  {/* Riepilogo totali */}
                  {formData.canone_mensile && (
                    <div className="col-span-2 bg-slate-50 p-4 rounded-lg border">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Riepilogo Mensile</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {/* SDD - Addebito Inquilino */}
                        <div className="bg-blue-50 p-3 rounded border border-blue-200">
                          <span className="text-blue-700 font-medium">SDD IN (da Inquilino):</span>
                          <p className="font-bold text-lg text-blue-800">
                            € {(
                              parseFloat(formData.canone_mensile || 0) + 
                              parseFloat(formData.spese_condominiali || 0) + 
                              parseFloat(formData.spese_forfettarie || 0) +
                              parseFloat(formData.quota_bollo || 0) +
                              Math.max(0, parseFloat(calculateMoneyValue(formData.quota_semplificaffitto_inquilino_perc, formData.canone_mensile)) - parseFloat(formData.sconto_sa_inquilino || 0))
                            ).toFixed(2)}
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            Canone + Condominio + Bollo + (Quota SA - Sconto)
                          </p>
                        </div>
                        {/* SEPA - Bonifico Proprietario */}
                        <div className="bg-green-50 p-3 rounded border border-green-200">
                          <span className="text-green-700 font-medium">SEPA OUT (al Proprietario):</span>
                          <p className="font-bold text-lg text-green-800">
                            € {(
                              parseFloat(formData.canone_mensile || 0) + 
                              parseFloat(formData.spese_condominiali || 0) + 
                              parseFloat(formData.spese_forfettarie || 0) -
                              Math.max(0, parseFloat(calculateMoneyValue(formData.quota_semplificaffitto_proprietario_perc, formData.canone_mensile)) - parseFloat(formData.sconto_sa_proprietario || 0))
                            ).toFixed(2)}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            Canone + Condominio - (Quota SA - Sconto)
                          </p>
                        </div>
                      </div>
                      {/* Dettaglio Quote SA con sconti */}
                      <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 gap-2 text-xs text-slate-500">
                        <div>
                          <span>Quota SA Inquilino ({formData.quota_semplificaffitto_inquilino_perc}%): </span>
                          <strong className="text-amber-600">€{calculateMoneyValue(formData.quota_semplificaffitto_inquilino_perc, formData.canone_mensile)}</strong>
                          {parseFloat(formData.sconto_sa_inquilino || 0) > 0 && (
                            <span className="text-red-500 ml-1">- €{parseFloat(formData.sconto_sa_inquilino).toFixed(2)} sconto</span>
                          )}
                        </div>
                        <div>
                          <span>Quota SA Proprietario ({formData.quota_semplificaffitto_proprietario_perc}%): </span>
                          <strong className="text-amber-600">€{calculateMoneyValue(formData.quota_semplificaffitto_proprietario_perc, formData.canone_mensile)}</strong>
                          {parseFloat(formData.sconto_sa_proprietario || 0) > 0 && (
                            <span className="text-red-500 ml-1">- €{parseFloat(formData.sconto_sa_proprietario).toFixed(2)} sconto</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Note */}
              <div>
                <Label>Note</Label>
                <Input
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  placeholder="Note aggiuntive..."
                />
              </div>

              {/* Codici Manuali per Contratti Esistenti */}
              <div className="form-section border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="usa_codici_manuali"
                    checked={formData.usa_codici_manuali}
                    onChange={(e) => setFormData({...formData, usa_codici_manuali: e.target.checked})}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="usa_codici_manuali" className="font-semibold flex items-center gap-2 cursor-pointer">
                    <Hash className="w-4 h-4" /> Inserisci codice contratto manuale (per contratti già esistenti)
                  </Label>
                </div>
                
                {formData.usa_codici_manuali && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-700 mb-3">
                      Usa questa sezione per importare contratti già esistenti con il loro codice originale.
                      Se lasci vuoto, il codice verrà generato automaticamente.
                    </p>
                    <div>
                      <Label>Codice Contratto Affitto</Label>
                      <Input
                        value={formData.codice_contratto_manuale}
                        onChange={(e) => setFormData({...formData, codice_contratto_manuale: e.target.value})}
                        placeholder="Es: 20260300001"
                        maxLength={11}
                        className="max-w-xs"
                      />
                      <p className="text-xs text-slate-500 mt-1">Formato: AAAAMMNNNNN (es. 20260300001)</p>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </ScrollArea>
          <DialogFooter className="flex justify-between">
            <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
              Annulla
            </Button>
            <div className="flex gap-2">
              {!editingItem && (
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => handleSubmitWithStato('in_compilazione')}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Salva Bozza
                </Button>
              )}
              <Button 
                type="button"
                onClick={() => handleSubmitWithStato('attivo')}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                {editingItem ? 'Aggiorna e Attiva' : 'Crea e Attiva'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog - Documenti Contratto */}
      <Dialog open={showDocumentiDialog} onOpenChange={setShowDocumentiDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Documenti Contratto</DialogTitle>
            <DialogDescription>
              {selectedContratto && getImmobileInfo(selectedContratto.property_id)}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh]">
            <div className="space-y-4">
              {/* Codice Contratto di Locazione */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Codice Contratto di Locazione</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Codice Contratto</p>
                      <p className="text-xs text-slate-500">Formato: AAAAMMNNNNN (es. 20260300001)</p>
                      {selectedContratto?.codice_contratto && (
                        <Badge className="mt-2 bg-slate-800 text-white font-mono text-base px-3 py-1">
                          {selectedContratto.codice_contratto}
                        </Badge>
                      )}
                    </div>
                    {!selectedContratto?.codice_contratto && (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Es: 20260300001"
                          id="codice_contratto_loc_input"
                          className="w-40 font-mono"
                          maxLength={11}
                        />
                        <Button
                          size="sm"
                          onClick={async () => {
                            const input = document.getElementById('codice_contratto_loc_input');
                            if (!input.value) {
                              toast.error('Inserisci un codice');
                              return;
                            }
                            try {
                              await axios.put(`${API_URL}/contracts/${selectedContratto.id}`, {
                                codice_contratto: input.value
                              });
                              toast.success('Codice contratto salvato');
                              fetchData(true);
                              // Aggiorna selectedContratto
                              setSelectedContratto({...selectedContratto, codice_contratto: input.value});
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Errore');
                            }
                          }}
                        >
                          Salva
                        </Button>
                      </div>
                    )}
                    {selectedContratto?.codice_contratto && (
                      <Badge className="bg-green-100 text-green-700">Assegnato</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Genera Mandato/Contratto Servizio */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Documenti Obbligatori per Gestione Pagamenti</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  
                  {/* SEZIONE LOCATORE - Contratto di Mandato di Servizio */}
                  <div className="p-4 border rounded-lg bg-slate-50/50">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium">Contratto di Mandato di Servizio</p>
                        <p className="text-xs text-slate-500">Legato alla proprietà (Locatore)</p>
                        {selectedContratto?.mandato_servizio_codice && (
                          <Badge className="mt-1 bg-green-100 text-green-700">{selectedContratto.mandato_servizio_codice}</Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {mandatoServizio?.codice && (
                          <Button variant="outline" size="sm" onClick={handleDownloadMandatoDocx}>
                            <FileDown className="w-4 h-4 mr-2" />
                            Scarica DOCX
                          </Button>
                        )}
                        <Button 
                          variant={mandatoServizio?.codice ? "outline" : "default"}
                          onClick={handleGeneraMandato}
                          size="sm"
                        >
                          <FileSignature className="w-4 h-4 mr-2" />
                          {mandatoServizio?.codice ? 'Già Generato' : 'Genera Mandato'}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Campo editabile Codice Mandato Servizio */}
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                        <Hash className="w-3 h-3" />
                        Codice Mandato Servizio (Locatore)
                      </p>
                      <div className="flex gap-2">
                        <Input
                          id="mandato_servizio_codice_input"
                          value={selectedContratto?.mandato_servizio_codice || ''}
                          onChange={(e) => setSelectedContratto(prev => ({...prev, mandato_servizio_codice: e.target.value}))}
                          placeholder="Es: L2024060003"
                          className="text-sm h-8"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const codice = selectedContratto?.mandato_servizio_codice || '';
                            if (!codice) { toast.error('Inserisci un codice'); return; }
                            try {
                              const res = await contrattiApi.update(selectedContratto.id, { mandato_servizio_codice: codice });
                              setSelectedContratto(res.data);
                              toast.success('Codice Mandato Servizio salvato');
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Errore');
                            }
                          }}
                        >
                          <Save className="w-3 h-3 mr-1" />
                          Salva
                        </Button>
                      </div>
                      {selectedContratto?.mandato_servizio_codice && (
                        <span className="text-xs text-green-600">Attuale: {selectedContratto.mandato_servizio_codice}</span>
                      )}
                    </div>
                    
                    {/* Dati SDD Locatore */}
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                        <CreditCard className="w-3 h-3" />
                        Dati Mandato SDD Locatore (per distinta bancaria)
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">ID Mandato SDD</Label>
                          <Input
                            id="mandato_sdd_locatore_codice_input"
                            value={selectedContratto?.mandato_sdd_locatore_codice || ''}
                            onChange={(e) => setSelectedContratto(prev => ({...prev, mandato_sdd_locatore_codice: e.target.value}))}
                            placeholder="Es: L20230500002"
                            className="text-sm h-8"
                          />
                          {selectedContratto?.mandato_sdd_locatore_codice ? (
                            <span className="text-xs text-green-600">Attuale: {selectedContratto.mandato_sdd_locatore_codice}</span>
                          ) : (
                            <span className="text-xs text-red-600">⚠ Manca ID Mandato SDD</span>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Data Firma</Label>
                          <Input
                            id="mandato_sdd_locatore_data_input"
                            type="date"
                            value={selectedContratto?.mandato_sdd_locatore_data_firma || ''}
                            onChange={(e) => setSelectedContratto(prev => ({...prev, mandato_sdd_locatore_data_firma: e.target.value}))}
                            className="text-sm h-8"
                          />
                          {selectedContratto?.mandato_sdd_locatore_data_firma ? (
                            <span className="text-xs text-green-600">Attuale: {new Date(selectedContratto.mandato_sdd_locatore_data_firma).toLocaleDateString('it-IT')}</span>
                          ) : (
                            <span className="text-xs text-red-600">⚠ Obbligatoria per distinta SDD</span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full"
                        onClick={async () => {
                          const codice = selectedContratto?.mandato_sdd_locatore_codice || '';
                          const dataFirma = selectedContratto?.mandato_sdd_locatore_data_firma || '';
                          if (!codice && !dataFirma) { toast.error('Inserisci almeno un valore'); return; }
                          try {
                            const updateData = {};
                            if (codice) updateData.mandato_sdd_locatore_codice = codice;
                            if (dataFirma) updateData.mandato_sdd_locatore_data_firma = dataFirma;
                            const res = await contrattiApi.update(selectedContratto.id, updateData);
                            setSelectedContratto(res.data);
                            toast.success('Dati SDD Locatore salvati');
                          } catch (error) {
                            console.error('Error saving SDD Locatore:', error);
                            toast.error(error.response?.data?.detail || 'Errore');
                          }
                        }}
                      >
                        <Save className="w-3 h-3 mr-1" />
                        Salva SDD Locatore
                      </Button>
                    </div>
                  </div>
                  
                  {/* SEZIONE CONDUTTORE - Contratto per Fornitura di Servizi */}
                  <div className="p-4 border rounded-lg bg-slate-50/50">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium">Contratto per Fornitura di Servizi</p>
                        <p className="text-xs text-slate-500">Legato agli inquilini (Conduttore)</p>
                        {selectedContratto?.contratto_servizio_codice && (
                          <Badge className="mt-1 bg-green-100 text-green-700">{selectedContratto.contratto_servizio_codice}</Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {contrattoServizio?.codice && (
                          <Button variant="outline" size="sm" onClick={handleDownloadContrattoDocx}>
                            <FileDown className="w-4 h-4 mr-2" />
                            Scarica DOCX
                          </Button>
                        )}
                        <Button 
                          variant={contrattoServizio?.codice ? "outline" : "default"}
                          onClick={handleGeneraContrattoServizio}
                          size="sm"
                        >
                          <FileSignature className="w-4 h-4 mr-2" />
                          {contrattoServizio?.codice ? 'Già Generato' : 'Genera Contratto'}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Campo editabile Codice Contratto Servizio */}
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                        <Hash className="w-3 h-3" />
                        Codice Contratto Servizio (Conduttore)
                      </p>
                      <div className="flex gap-2">
                        <Input
                          id="contratto_servizio_codice_input"
                          value={selectedContratto?.contratto_servizio_codice || ''}
                          onChange={(e) => setSelectedContratto(prev => ({...prev, contratto_servizio_codice: e.target.value}))}
                          placeholder="Es: C2024060003"
                          className="text-sm h-8"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const codice = selectedContratto?.contratto_servizio_codice || '';
                            if (!codice) { toast.error('Inserisci un codice'); return; }
                            try {
                              const res = await contrattiApi.update(selectedContratto.id, { contratto_servizio_codice: codice });
                              setSelectedContratto(res.data);
                              toast.success('Codice Contratto Servizio salvato');
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Errore');
                            }
                          }}
                        >
                          <Save className="w-3 h-3 mr-1" />
                          Salva
                        </Button>
                      </div>
                      {selectedContratto?.contratto_servizio_codice && (
                        <span className="text-xs text-green-600">Attuale: {selectedContratto.contratto_servizio_codice}</span>
                      )}
                    </div>
                    
                    {/* Dati SDD Conduttore */}
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                        <CreditCard className="w-3 h-3" />
                        Dati Mandato SDD Conduttore (per distinta bancaria)
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">ID Mandato SDD</Label>
                          <Input
                            id="mandato_sdd_conduttore_codice_input"
                            value={selectedContratto?.mandato_sdd_conduttore_codice || ''}
                            onChange={(e) => setSelectedContratto(prev => ({...prev, mandato_sdd_conduttore_codice: e.target.value}))}
                            placeholder="Es: C20230500002 o 000001201200004"
                            className="text-sm h-8"
                          />
                          {selectedContratto?.mandato_sdd_conduttore_codice ? (
                            <span className="text-xs text-green-600">Attuale: {selectedContratto.mandato_sdd_conduttore_codice}</span>
                          ) : (
                            <span className="text-xs text-red-600">⚠ Manca ID Mandato SDD</span>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Data Firma</Label>
                          <Input
                            id="mandato_sdd_conduttore_data_input"
                            type="date"
                            value={selectedContratto?.mandato_sdd_conduttore_data_firma || ''}
                            onChange={(e) => setSelectedContratto(prev => ({...prev, mandato_sdd_conduttore_data_firma: e.target.value}))}
                            className="text-sm h-8"
                          />
                          {selectedContratto?.mandato_sdd_conduttore_data_firma ? (
                            <span className="text-xs text-green-600">Attuale: {new Date(selectedContratto.mandato_sdd_conduttore_data_firma).toLocaleDateString('it-IT')}</span>
                          ) : (
                            <span className="text-xs text-red-600">⚠ Obbligatoria per distinta SDD</span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full"
                        onClick={async () => {
                          const codice = selectedContratto?.mandato_sdd_conduttore_codice || '';
                          const dataFirma = selectedContratto?.mandato_sdd_conduttore_data_firma || '';
                          if (!codice && !dataFirma) { toast.error('Inserisci almeno un valore'); return; }
                          try {
                            const updateData = {};
                            if (codice) updateData.mandato_sdd_conduttore_codice = codice;
                            if (dataFirma) updateData.mandato_sdd_conduttore_data_firma = dataFirma;
                            const res = await contrattiApi.update(selectedContratto.id, updateData);
                            setSelectedContratto(res.data);
                            toast.success('Dati SDD Conduttore salvati');
                          } catch (error) {
                            console.error('Error saving SDD Conduttore:', error);
                            toast.error(error.response?.data?.detail || 'Errore');
                          }
                        }}
                      >
                        <Save className="w-3 h-3 mr-1" />
                        Salva SDD Conduttore
                      </Button>
                    </div>
                  </div>

                  {/* SEZIONE GARANTE - Solo Dati Mandato SDD (se presente garante) */}
                  {selectedContratto?.garanti_ids && selectedContratto.garanti_ids.length > 0 && (
                    <div className="p-4 border rounded-lg bg-amber-50/50 border-amber-200">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium">Mandato SDD Garante</p>
                          <p className="text-xs text-slate-500">
                            Garante: {users.find(u => u.id === selectedContratto.garanti_ids[0])?.nome} {users.find(u => u.id === selectedContratto.garanti_ids[0])?.cognome}
                          </p>
                        </div>
                      </div>
                      
                      {/* Dati SDD Garante */}
                      <div className="mt-2">
                        <p className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          Dati Mandato SDD Garante (per distinta bancaria)
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">ID Mandato SDD</Label>
                            <Input
                              id="mandato_sdd_garante_codice_input"
                              value={selectedContratto?.mandato_sdd_garante_codice || ''}
                              onChange={(e) => setSelectedContratto(prev => ({...prev, mandato_sdd_garante_codice: e.target.value}))}
                              placeholder="Es: C20230500002 (stesso del conduttore)"
                              className="text-sm h-8"
                            />
                            {selectedContratto?.mandato_sdd_garante_codice ? (
                              <span className="text-xs text-green-600">Attuale: {selectedContratto.mandato_sdd_garante_codice}</span>
                            ) : (
                              <span className="text-xs text-red-600">⚠ Manca ID Mandato SDD</span>
                            )}
                          </div>
                          <div>
                            <Label className="text-xs">Data Firma</Label>
                            <Input
                              id="mandato_sdd_garante_data_input"
                              type="date"
                              value={selectedContratto?.mandato_sdd_garante_data_firma || ''}
                              onChange={(e) => setSelectedContratto(prev => ({...prev, mandato_sdd_garante_data_firma: e.target.value}))}
                              className="text-sm h-8"
                            />
                            {selectedContratto?.mandato_sdd_garante_data_firma ? (
                              <span className="text-xs text-green-600">Attuale: {new Date(selectedContratto.mandato_sdd_garante_data_firma).toLocaleDateString('it-IT')}</span>
                            ) : (
                              <span className="text-xs text-red-600">⚠ Obbligatoria per distinta SDD</span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 w-full"
                          onClick={async () => {
                            const codice = selectedContratto?.mandato_sdd_garante_codice || '';
                            const dataFirma = selectedContratto?.mandato_sdd_garante_data_firma || '';
                            if (!codice && !dataFirma) { toast.error('Inserisci almeno un valore'); return; }
                            try {
                              const updateData = {};
                              if (codice) updateData.mandato_sdd_garante_codice = codice;
                              if (dataFirma) updateData.mandato_sdd_garante_data_firma = dataFirma;
                              const res = await contrattiApi.update(selectedContratto.id, updateData);
                              setSelectedContratto(res.data);
                              toast.success('Dati SDD Garante salvati');
                            } catch (error) {
                              console.error('Error saving SDD Garante:', error);
                              toast.error(error.response?.data?.detail || 'Errore');
                            }
                          }}
                        >
                          <Save className="w-3 h-3 mr-1" />
                          Salva SDD Garante
                        </Button>
                      </div>
                    </div>
                  )}

                  {(!mandatoServizio?.codice || !contrattoServizio?.codice) && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm text-orange-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Genera entrambi i documenti per abilitare la gestione pagamenti
                      </p>
                    </div>
                  )}

                  {/* Cronologia modifiche codici (audit) */}
                  <CodiciHistoryPanel contractId={selectedContratto?.id} />

                </CardContent>
              </Card>

              {/* Dati Registrazione Contratto */}
              <Card className="border-blue-200 bg-blue-50/40">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2 text-blue-800">
                    <FileText className="w-4 h-4" /> Dati Registrazione Contratto
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Dati attuali in atti (se presenti) */}
                  {selectedContratto?.registrazione_contratto && (
                    <div className="mb-4 p-3 bg-white rounded-lg border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-blue-700">Dati in atti:</span>
                        <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5">In Atti</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                        {selectedContratto.registrazione_contratto.ufficio_territoriale && (
                          <div><span className="text-slate-500">Ufficio:</span> <strong className="text-green-700">{selectedContratto.registrazione_contratto.ufficio_territoriale}</strong></div>
                        )}
                        {selectedContratto.registrazione_contratto.data_registrazione && (
                          <div><span className="text-slate-500">Data:</span> <strong className="text-green-700">{new Date(selectedContratto.registrazione_contratto.data_registrazione).toLocaleDateString('it-IT')}</strong></div>
                        )}
                        {selectedContratto.registrazione_contratto.protocollo && (
                          <div><span className="text-slate-500">Protocollo:</span> <strong className="text-green-700">{selectedContratto.registrazione_contratto.protocollo}</strong></div>
                        )}
                        {selectedContratto.registrazione_contratto.codice_identificativo && (
                          <div><span className="text-slate-500">Cod. Ident.:</span> <strong className="text-green-700">{selectedContratto.registrazione_contratto.codice_identificativo}</strong></div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Form editabile */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Ufficio Territoriale di</Label>
                      <Input
                        value={regData.ufficio_territoriale}
                        onChange={(e) => setRegData({...regData, ufficio_territoriale: e.target.value})}
                        placeholder="es. Firenze, Milano..."
                        className="h-8 text-sm"
                        data-testid="reg-edit-ufficio"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Data di Registrazione</Label>
                      <Input
                        type="date"
                        value={regData.data_registrazione}
                        onChange={(e) => setRegData({...regData, data_registrazione: e.target.value})}
                        className="h-8 text-sm"
                        data-testid="reg-edit-data"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Protocollo</Label>
                      <Input
                        value={regData.protocollo}
                        onChange={(e) => setRegData({...regData, protocollo: e.target.value})}
                        placeholder="Protocollo..."
                        className="h-8 text-sm"
                        data-testid="reg-edit-protocollo"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Codice Identificativo</Label>
                      <Input
                        value={regData.codice_identificativo}
                        onChange={(e) => setRegData({...regData, codice_identificativo: e.target.value})}
                        placeholder="Codice identificativo..."
                        className="h-8 text-sm"
                        data-testid="reg-edit-codice"
                      />
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="mt-3 bg-blue-600 hover:bg-blue-700"
                    disabled={savingReg || (!regData.ufficio_territoriale && !regData.data_registrazione && !regData.protocollo && !regData.codice_identificativo)}
                    onClick={async () => {
                      setSavingReg(true);
                      try {
                        await axios.put(`${API_URL}/contracts/${selectedContratto.id}/registrazione`, regData);
                        toast.success('Dati registrazione aggiornati');
                        fetchData(true);
                        // Aggiorna selectedContratto locale
                        setSelectedContratto(prev => ({...prev, registrazione_contratto: {...prev?.registrazione_contratto, ...regData}}));
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Errore aggiornamento');
                      } finally {
                        setSavingReg(false);
                      }
                    }}
                    data-testid="reg-save-btn"
                  >
                    {selectedContratto?.registrazione_contratto ? 'Sovrascrivi' : 'Salva Registrazione'}
                  </Button>
                </CardContent>
              </Card>

              {/* Upload nuovo documento */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Upload className="w-4 h-4" /> Carica Documento
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Tipo Documento *</Label>
                      <Select 
                        value={uploadDocData.tipo_documento} 
                        onValueChange={(v) => setUploadDocData({...uploadDocData, tipo_documento: v})}
                      >
                        <SelectTrigger><SelectValue placeholder="Seleziona tipo..." /></SelectTrigger>
                        <SelectContent>
                          {tipiDocumento.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>File *</Label>
                      <Input 
                        type="file" 
                        onChange={(e) => setUploadDocData({...uploadDocData, file: e.target.files[0]})}
                      />
                    </div>
                    <div>
                      <Label>Note</Label>
                      <Input 
                        value={uploadDocData.note}
                        onChange={(e) => setUploadDocData({...uploadDocData, note: e.target.value})}
                        placeholder="Note opzionali..."
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={handleUploadDocumento} 
                    className="mt-3"
                    disabled={!uploadDocData.tipo_documento || !uploadDocData.file}
                  >
                    <Upload className="w-4 h-4 mr-2" /> Carica
                  </Button>
                </CardContent>
              </Card>

              {/* Lista documenti caricati */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Documenti Allegati ({documentiContratto.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {documentiContratto.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Nessun documento allegato</p>
                  ) : (
                    <div className="space-y-2">
                      {documentiContratto.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">
                              {tipiDocumento.find(t => t.value === doc.tipo_documento)?.label || doc.tipo_documento}
                            </p>
                            <p className="text-xs text-slate-500">{doc.filename}</p>
                            {doc.note && <p className="text-xs text-slate-400 mt-1">{doc.note}</p>}
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownloadDocumento(doc.id, doc.filename)}
                              data-testid={`download-contratto-doc-${doc.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteDocumento(doc.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dialog - Subentro */}
      <Dialog open={showSubentroDialog} onOpenChange={setShowSubentroDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Subentro Inquilino</DialogTitle>
            <DialogDescription>
              Sostituisci un inquilino con un nuovo soggetto
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Inquilino Uscente *</Label>
              <Combobox
                options={(selectedContratto?.inquilini_ids || [selectedContratto?.inquilino_id]).filter(Boolean).map(id => ({
                  value: id,
                  label: getUserInfo(id)
                }))}
                value={subentroData.vecchio_inquilino_id}
                onValueChange={(v) => setSubentroData({...subentroData, vecchio_inquilino_id: v})}
                placeholder="Seleziona..."
              />
            </div>
            <div>
              <Label>Nuovo Inquilino *</Label>
              <Combobox
                options={userOptions.filter(u => 
                  !(selectedContratto?.inquilini_ids || [selectedContratto?.inquilino_id]).includes(u.value)
                )}
                value={subentroData.nuovo_inquilino_id}
                onValueChange={(v) => setSubentroData({...subentroData, nuovo_inquilino_id: v})}
                placeholder="Cerca e seleziona..."
                searchPlaceholder="Cerca per nome..."
              />
            </div>
            <div>
              <Label>Data Subentro *</Label>
              <Input
                type="date"
                value={subentroData.data_subentro}
                onChange={(e) => setSubentroData({...subentroData, data_subentro: e.target.value})}
              />
            </div>
            <div>
              <Label>Note</Label>
              <Input
                value={subentroData.note}
                onChange={(e) => setSubentroData({...subentroData, note: e.target.value})}
                placeholder="Es: Subentro per decesso"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubentroDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSubentro}>
              Registra Subentro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nuovo Inquilino Completo */}
      <Dialog open={showNewInquilinoDialog} onOpenChange={setShowNewInquilinoDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Inquilino</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Dati Anagrafici */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Dati Anagrafici</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome *</Label>
                  <Input 
                    value={newInquilinoData.nome}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, nome: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Cognome *</Label>
                  <Input 
                    value={newInquilinoData.cognome}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, cognome: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Data Nascita</Label>
                  <Input 
                    type="date"
                    value={newInquilinoData.data_nascita}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, data_nascita: e.target.value})}
                  />
                </div>
                <div className="relative">
                  <Label>Luogo Nascita</Label>
                  <Input 
                    value={newInquilinoData.luogo_nascita}
                    onChange={(e) => {
                      setNewInquilinoData({...newInquilinoData, luogo_nascita: e.target.value});
                      searchComuniNascitaNewUser(e.target.value);
                    }}
                    placeholder="Cerca comune..."
                  />
                  {newUserComuniNascitaSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-auto">
                      {newUserComuniNascitaSuggestions.map((c, idx) => (
                        <div key={`${c.nome}-${c.sigla_provincia || c.provincia || ''}-${idx}`} className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                          onClick={() => {
                            setNewInquilinoData({...newInquilinoData, luogo_nascita: c.nome, provincia_nascita: c.sigla_provincia || c.provincia});
                            setNewUserComuniNascitaSuggestions([]);
                          }}>
                          {c.nome} ({c.sigla_provincia || c.provincia})
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Prov. Nascita</Label>
                  <Input 
                    value={newInquilinoData.provincia_nascita}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, provincia_nascita: e.target.value.toUpperCase()})}
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label>Codice Fiscale</Label>
                  <Input 
                    value={newInquilinoData.codice_fiscale}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, codice_fiscale: e.target.value.toUpperCase()})}
                    maxLength={16}
                  />
                </div>
              </div>
            </div>

            {/* Contatti */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Contatti</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input 
                    type="email"
                    value={newInquilinoData.email}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input 
                    value={newInquilinoData.telefono}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, telefono: e.target.value})}
                  />
                </div>
                <div>
                  <Label>PEC</Label>
                  <Input 
                    type="email"
                    value={newInquilinoData.pec}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, pec: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Altro Contatto</Label>
                  <Input 
                    value={newInquilinoData.altro_contatto}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, altro_contatto: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Residenza */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Residenza</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Indirizzo</Label>
                  <Input 
                    value={newInquilinoData.indirizzo_residenza}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, indirizzo_residenza: e.target.value})}
                    placeholder="Via/Piazza..."
                  />
                </div>
                <div className="relative">
                  <Label>Città</Label>
                  <Input 
                    value={newInquilinoData.citta_residenza}
                    onChange={(e) => {
                      setNewInquilinoData({...newInquilinoData, citta_residenza: e.target.value});
                      searchComuniNewUser(e.target.value);
                    }}
                    placeholder="Cerca comune..."
                  />
                  {newUserComuniSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-auto">
                      {newUserComuniSuggestions.map((c, idx) => (
                        <div key={`${c.nome}-${c.sigla_provincia || c.provincia || ''}-${idx}`} className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                          onClick={() => {
                            const capList = c.cap_list || [];
                            setNewInquilinoData({
                              ...newInquilinoData, 
                              citta_residenza: c.nome, 
                              provincia_residenza: c.sigla_provincia || c.provincia,
                              cap_residenza: capList.length === 1 ? capList[0] : ''
                            });
                            setNewUserComuniSuggestions([]);
                          }}>
                          {c.nome} ({c.sigla_provincia || c.provincia}) - CAP: {c.cap}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>CAP</Label>
                  <Input 
                    value={newInquilinoData.cap_residenza}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, cap_residenza: e.target.value})}
                    maxLength={5}
                  />
                </div>
                <div>
                  <Label>Provincia</Label>
                  <Input 
                    value={newInquilinoData.provincia_residenza}
                    onChange={(e) => setNewInquilinoData({...newInquilinoData, provincia_residenza: e.target.value.toUpperCase()})}
                    maxLength={2}
                  />
                </div>
              </div>
            </div>

            {/* Dati Bancari */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Dati Bancari</h4>
              <div>
                <Label>IBAN (per addebiti SDD)</Label>
                <Input 
                  value={newInquilinoData.iban}
                  onChange={(e) => setNewInquilinoData({...newInquilinoData, iban: e.target.value.toUpperCase()})}
                  placeholder="IT60X0542811101000000123456"
                  maxLength={27}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewInquilinoDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateInquilino}>
              Crea e Seleziona
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nuovo Garante Completo */}
      <Dialog open={showNewGaranteDialog} onOpenChange={setShowNewGaranteDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Garante</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Dati Anagrafici */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Dati Anagrafici</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome *</Label>
                  <Input 
                    value={newGaranteData.nome}
                    onChange={(e) => setNewGaranteData({...newGaranteData, nome: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Cognome *</Label>
                  <Input 
                    value={newGaranteData.cognome}
                    onChange={(e) => setNewGaranteData({...newGaranteData, cognome: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Data Nascita</Label>
                  <Input 
                    type="date"
                    value={newGaranteData.data_nascita}
                    onChange={(e) => setNewGaranteData({...newGaranteData, data_nascita: e.target.value})}
                  />
                </div>
                <div className="relative">
                  <Label>Luogo Nascita</Label>
                  <Input 
                    value={newGaranteData.luogo_nascita}
                    onChange={(e) => {
                      setNewGaranteData({...newGaranteData, luogo_nascita: e.target.value});
                      searchComuniNascitaNewUser(e.target.value);
                    }}
                    placeholder="Cerca comune..."
                  />
                  {newUserComuniNascitaSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-auto">
                      {newUserComuniNascitaSuggestions.map((c, idx) => (
                        <div key={`${c.nome}-${c.sigla_provincia || c.provincia || ''}-${idx}`} className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                          onClick={() => {
                            setNewGaranteData({...newGaranteData, luogo_nascita: c.nome, provincia_nascita: c.sigla_provincia || c.provincia});
                            setNewUserComuniNascitaSuggestions([]);
                          }}>
                          {c.nome} ({c.sigla_provincia || c.provincia})
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Prov. Nascita</Label>
                  <Input 
                    value={newGaranteData.provincia_nascita}
                    onChange={(e) => setNewGaranteData({...newGaranteData, provincia_nascita: e.target.value.toUpperCase()})}
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label>Codice Fiscale</Label>
                  <Input 
                    value={newGaranteData.codice_fiscale}
                    onChange={(e) => setNewGaranteData({...newGaranteData, codice_fiscale: e.target.value.toUpperCase()})}
                    maxLength={16}
                  />
                </div>
              </div>
            </div>

            {/* Contatti */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Contatti</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={newGaranteData.email}
                    onChange={(e) => setNewGaranteData({...newGaranteData, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input 
                    value={newGaranteData.telefono}
                    onChange={(e) => setNewGaranteData({...newGaranteData, telefono: e.target.value})}
                  />
                </div>
                <div>
                  <Label>PEC</Label>
                  <Input 
                    type="email"
                    value={newGaranteData.pec}
                    onChange={(e) => setNewGaranteData({...newGaranteData, pec: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Altro Contatto</Label>
                  <Input 
                    value={newGaranteData.altro_contatto}
                    onChange={(e) => setNewGaranteData({...newGaranteData, altro_contatto: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Residenza */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Residenza</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Indirizzo</Label>
                  <Input 
                    value={newGaranteData.indirizzo_residenza}
                    onChange={(e) => setNewGaranteData({...newGaranteData, indirizzo_residenza: e.target.value})}
                    placeholder="Via/Piazza..."
                  />
                </div>
                <div className="relative">
                  <Label>Città</Label>
                  <Input 
                    value={newGaranteData.citta_residenza}
                    onChange={(e) => {
                      setNewGaranteData({...newGaranteData, citta_residenza: e.target.value});
                      searchComuniNewUser(e.target.value);
                    }}
                    placeholder="Cerca comune..."
                  />
                  {newUserComuniSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-auto">
                      {newUserComuniSuggestions.map((c, idx) => (
                        <div key={`${c.nome}-${c.sigla_provincia || c.provincia || ''}-${idx}`} className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                          onClick={() => {
                            const capList = c.cap_list || [];
                            setNewGaranteData({
                              ...newGaranteData, 
                              citta_residenza: c.nome, 
                              provincia_residenza: c.sigla_provincia || c.provincia,
                              cap_residenza: capList.length === 1 ? capList[0] : ''
                            });
                            setNewUserComuniSuggestions([]);
                          }}>
                          {c.nome} ({c.sigla_provincia || c.provincia}) - CAP: {c.cap}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>CAP</Label>
                  <Input 
                    value={newGaranteData.cap_residenza}
                    onChange={(e) => setNewGaranteData({...newGaranteData, cap_residenza: e.target.value})}
                    maxLength={5}
                  />
                </div>
                <div>
                  <Label>Provincia</Label>
                  <Input 
                    value={newGaranteData.provincia_residenza}
                    onChange={(e) => setNewGaranteData({...newGaranteData, provincia_residenza: e.target.value.toUpperCase()})}
                    maxLength={2}
                  />
                </div>
              </div>
            </div>

            {/* Dati Bancari */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Dati Bancari</h4>
              <div>
                <Label>IBAN (per addebiti SDD)</Label>
                <Input 
                  value={newGaranteData.iban}
                  onChange={(e) => setNewGaranteData({...newGaranteData, iban: e.target.value.toUpperCase()})}
                  placeholder="IT60X0542811101000000123456"
                  maxLength={27}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewGaranteDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateGarante}>
              Crea e Seleziona
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nuovo Immobile Rapido */}
      <Dialog open={showNewImmobileDialog} onOpenChange={setShowNewImmobileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Immobile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Indirizzo *</Label>
              <Input 
                value={newImmobileData.indirizzo}
                onChange={(e) => setNewImmobileData({...newImmobileData, indirizzo: e.target.value})}
                placeholder="Via Roma 1"
                data-testid="new-immobile-indirizzo"
              />
            </div>

            {/* Comune con autocomplete */}
            <div className="relative">
              <Label>Comune *</Label>
              <Input 
                value={newImmobileData.citta}
                onChange={(e) => {
                  setNewImmobileData({...newImmobileData, citta: e.target.value});
                  searchComuni(e.target.value);
                }}
                placeholder="Cerca comune..."
                data-testid="new-immobile-citta"
              />
              {comuniSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                  {comuniSuggestions.map((comune, idx) => (
                    <div 
                      key={`${comune.nome}-${comune.provincia || ''}-${idx}`}
                      className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                      onClick={() => handleSelectComune(comune)}
                    >
                      <span className="font-medium">{comune.nome}</span>
                      <span className="text-slate-500 ml-2">({comune.provincia})</span>
                      <span className="text-slate-400 ml-2">- {comune.cap}</span>
                    </div>
                  ))}
                </div>
              )}
              {searchingComuni && <p className="text-xs text-slate-500 mt-1">Ricerca in corso...</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CAP *</Label>
                <Input 
                  value={newImmobileData.cap}
                  onChange={(e) => setNewImmobileData({...newImmobileData, cap: e.target.value})}
                  placeholder="00100"
                  maxLength={5}
                  data-testid="new-immobile-cap"
                />
              </div>
              <div>
                <Label>Provincia *</Label>
                <Input 
                  value={newImmobileData.provincia}
                  onChange={(e) => setNewImmobileData({...newImmobileData, provincia: e.target.value.toUpperCase()})}
                  placeholder="RM"
                  maxLength={2}
                  data-testid="new-immobile-provincia"
                />
              </div>
            </div>

            {/* Proprietari */}
            <div>
              <Label>Proprietari *</Label>
              <p className="text-xs text-slate-500 mb-2">Seleziona uno o più proprietari per questo immobile</p>
              <ComboboxMulti
                options={userOptions}
                values={newImmobileData.proprietari_ids}
                onValuesChange={(ids) => setNewImmobileData({...newImmobileData, proprietari_ids: ids})}
                placeholder="Cerca e seleziona proprietari..."
                searchPlaceholder="Cerca per nome..."
              />
              {newImmobileData.proprietari_ids.length > 0 && (
                <p className="text-sm text-green-600 mt-2">
                  {newImmobileData.proprietari_ids.length} proprietario/i selezionato/i
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowNewImmobileDialog(false);
              setNewImmobileData({ indirizzo: '', citta: '', cap: '', provincia: '', proprietari_ids: [] });
              setComuniSuggestions([]);
            }}>
              Annulla
            </Button>
            <Button onClick={handleCreateImmobile} data-testid="create-immobile-btn">
              Crea e Seleziona
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Sblocco Dati Economici */}
      <Dialog open={showUnlockConfirm} onOpenChange={setShowUnlockConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Unlock className="w-5 h-5" />
              Sblocca Dati Economici
            </DialogTitle>
            <DialogDescription>
              Stai per sbloccare i dati economici del contratto per modificarli.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-3">
              Questa azione è necessaria per:
            </p>
            <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
              <li>Adeguamento ISTAT del canone</li>
              <li>Modifica spese condominiali</li>
              <li>Correzione quote o sconti</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4 text-sm text-amber-700">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Le modifiche saranno registrate nello storico con data e valori precedenti.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnlockConfirm(false)}>
              Annulla
            </Button>
            <Button onClick={handleUnlockDatiEconomici} className="bg-amber-600 hover:bg-amber-700">
              Sì, Sblocca
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Timeline Modifiche */}
      <Dialog open={showTimelineDialog} onOpenChange={setShowTimelineDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Storico Modifiche Economiche
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-96">
            {timelineContratto.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Nessuna modifica registrata</p>
            ) : (
              <div className="space-y-4">
                {timelineContratto.map((entry, idx) => (
                  <div key={`${entry.data || ''}-${entry.campo_modificato || ''}-${idx}`} className="border-l-2 border-blue-300 pl-4 pb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">
                        {new Date(entry.data).toLocaleDateString('it-IT', { 
                          day: '2-digit', month: '2-digit', year: 'numeric' 
                        })}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {new Date(entry.data).toLocaleTimeString('it-IT', { 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-slate-700">{entry.campo_modificato}</p>
                    <div className="flex gap-4 text-sm mt-1">
                      <span className="text-red-500">Da: {entry.valore_precedente}</span>
                      <span className="text-green-600">A: {entry.valore_nuovo}</span>
                    </div>
                    {entry.note && (
                      <p className="text-xs text-slate-500 mt-1">{entry.note}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimelineDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Cessazione Contratto */}
      <Dialog open={showCessaDialog} onOpenChange={setShowCessaDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-orange-600" />
              Cessa Contratto
            </DialogTitle>
            <DialogDescription>
              Inserisci la data di fine validità del contratto. 
              Dopo questa data non verranno più generati pagamenti ordinari.
              I pagamenti straordinari resteranno attivi per adempimenti futuri.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="data-cessazione" className="text-sm font-medium">
                Data Cessazione *
              </Label>
              <Input
                id="data-cessazione"
                type="date"
                value={dataCessazione}
                onChange={(e) => setDataCessazione(e.target.value)}
                data-testid="input-data-cessazione"
              />
              <p className="text-xs text-slate-500">
                Esempio: se inserisci 31/03/2026, dal 01/04/2026 non verranno elaborati pagamenti ordinari
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="motivo-cessazione" className="text-sm font-medium">
                Motivo (opzionale)
              </Label>
              <Input
                id="motivo-cessazione"
                placeholder="Es: Fine locazione, Disdetta anticipata..."
                value={motivoCessazione}
                onChange={(e) => setMotivoCessazione(e.target.value)}
                data-testid="input-motivo-cessazione"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCessaDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCessaContratto}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="btn-conferma-cessazione"
            >
              <Ban className="w-4 h-4 mr-2" />
              Conferma Cessazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Storico Pagamenti - Piano Teorico */}
      <Dialog open={showStoricoPagamentiDialog} onOpenChange={setShowStoricoPagamentiDialog}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="storico-pagamenti-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Storico Pagamenti - {selectedContratto?.codice_contratto}
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between" asChild>
              <div>
                <span className="flex items-center gap-3 text-sm">
                  {selectedContratto && getImmobileInfo(selectedContratto.property_id)}
                  {storicoPagamentiData?.contratto && (
                    <span className="text-slate-400">
                      | {storicoPagamentiData.contratto.tipo_contratto} | {storicoPagamentiData.contratto.totale_rate} mensilità
                      {storicoPagamentiData.contratto.is_mid_month && ' (metà mese)'}
                    </span>
                  )}
                </span>
                <span className="flex gap-2">
                <Button variant="outline" size="sm" data-testid="export-storico-xlsx-btn"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_URL}/contracts/${selectedContratto.id}/export-storico/xlsx`, {
                        credentials: 'include'
                      });
                      if (!res.ok) throw new Error('Export failed');
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `storico_${selectedContratto.codice_contratto}.xlsx`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('Export XLSX completato');
                    } catch { toast.error('Errore export XLSX'); }
                  }}>
                  <FileSpreadsheet className="w-4 h-4 mr-1" /> XLSX
                </Button>
                <Button variant="outline" size="sm" data-testid="export-storico-pdf-btn"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_URL}/contracts/${selectedContratto.id}/export-storico/pdf`, {
                        credentials: 'include'
                      });
                      if (!res.ok) throw new Error('Export failed');
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `storico_${selectedContratto.codice_contratto}.pdf`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('Export PDF completato');
                    } catch { toast.error('Errore export PDF'); }
                  }}>
                  <FileDown className="w-4 h-4 mr-1" /> PDF
                </Button>
                {/* Import fatture CSV */}
                <label className="cursor-pointer">
                  <input type="file" accept=".csv" className="hidden" data-testid="import-fatture-csv-input"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const res = await axios.post(`${API_URL}/contracts/${selectedContratto.id}/import-fatture-csv`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        const d = res.data;
                        toast.success(`CSV: ${d.matched} fatture associate${d.straordinari ? `, ${d.straordinari} straordinari` : ''}, ${d.unmatched} non associate`);
                        // Reload storico
                        const stRes = await axios.get(`${API_URL}/contracts/${selectedContratto.id}/pagamenti-storico`);
                        setStoricoPagamentiData(stRes.data);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Errore importazione CSV');
                      }
                      e.target.value = '';
                    }} />
                  <Button variant="outline" size="sm" asChild data-testid="import-fatture-csv-btn">
                    <span><FileSpreadsheet className="w-4 h-4 mr-1 text-amber-600" /> Importa CSV</span>
                  </Button>
                </label>
                {/* Import fatture PDF bulk */}
                <label className="cursor-pointer">
                  <input type="file" accept=".pdf" multiple className="hidden" data-testid="import-fatture-pdf-input"
                    onChange={async (e) => {
                      const files = e.target.files;
                      if (!files || files.length === 0) return;
                      try {
                        const formData = new FormData();
                        for (let i = 0; i < files.length; i++) {
                          formData.append('files', files[i]);
                        }
                        toast.info(`Caricamento ${files.length} PDF in corso...`);
                        const res = await axios.post(`${API_URL}/contracts/${selectedContratto.id}/import-fatture-pdf`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        const d = res.data;
                        toast.success(`PDF: ${d.matched} associati, ${d.unmatched} non associati`);
                        if (d.dettaglio_unmatched?.length > 0) {
                          d.dettaglio_unmatched.forEach(u => toast.warning(`${u.filename}: ${u.motivo}`));
                        }
                        // Reload storico
                        const stRes = await axios.get(`${API_URL}/contracts/${selectedContratto.id}/pagamenti-storico`);
                        setStoricoPagamentiData(stRes.data);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Errore upload PDF');
                      }
                      e.target.value = '';
                    }} />
                  <Button variant="outline" size="sm" asChild data-testid="import-fatture-pdf-btn">
                    <span><Upload className="w-4 h-4 mr-1 text-red-600" /> Importa PDF</span>
                  </Button>
                </label>
                {/* Download massivo fatture unite (via email) */}
                <Button variant="outline" size="sm" data-testid="invia-fatture-locatore-btn"
                  disabled={invioFattureLoading === 'locatore'}
                  onClick={() => handleInviaFattureUnite('locatore')}
                  title="Unisci tutte le fatture del Locatore in un unico PDF e invialo via email">
                  <Mail className="w-4 h-4 mr-1 text-indigo-600" />
                  {invioFattureLoading === 'locatore' ? 'Invio…' : 'Fatture Locatore'}
                </Button>
                <Button variant="outline" size="sm" data-testid="invia-fatture-conduttore-btn"
                  disabled={invioFattureLoading === 'conduttore'}
                  onClick={() => handleInviaFattureUnite('conduttore')}
                  title="Unisci tutte le fatture del Conduttore in un unico PDF e invialo via email">
                  <Mail className="w-4 h-4 mr-1 text-teal-600" />
                  {invioFattureLoading === 'conduttore' ? 'Invio…' : 'Fatture Conduttore'}
                </Button>
                </span>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          {storicoPagamentiData && (
          <div className="space-y-4">
            {/* Riepilogo globale - solo mensilità concluse */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-lg" data-testid="storico-riepilogo">
              <div className="text-center">
                <p className="text-lg font-bold text-blue-600">
                  {storicoPagamentiData.riepilogo.totale_sdd_concluso.toLocaleString('it-IT', { minimumFractionDigits: 2, style: 'currency', currency: 'EUR' })}
                </p>
                <p className="text-xs text-slate-500">Totale SDD IN (concluse/pagate)</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-green-600">
                  {storicoPagamentiData.riepilogo.totale_sepa_concluso.toLocaleString('it-IT', { minimumFractionDigits: 2, style: 'currency', currency: 'EUR' })}
                </p>
                <p className="text-xs text-slate-500">Totale SEPA OUT (concluse/pagate)</p>
              </div>
              <div className="text-center">
                <p className={`text-lg font-bold ${(storicoPagamentiData.riepilogo.totale_sdd_concluso - storicoPagamentiData.riepilogo.totale_sepa_concluso) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {(storicoPagamentiData.riepilogo.totale_sdd_concluso - storicoPagamentiData.riepilogo.totale_sepa_concluso).toLocaleString('it-IT', { minimumFractionDigits: 2, style: 'currency', currency: 'EUR' })}
                </p>
                <p className="text-xs text-slate-500">Margine SA (concluse/pagate)</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-slate-700">{(storicoPagamentiData.riepilogo.rate_concluse || 0) + (storicoPagamentiData.riepilogo.rate_pagate || 0)}/{storicoPagamentiData.riepilogo.rate_totali}</p>
                <p className="text-xs text-slate-500">Concluse/Pagate</p>
              </div>
            </div>

            {/* Sezioni per annualità - ordine invertito (più recente in alto), nascondi annualità tutte future */}
            {[...storicoPagamentiData.annualita]
              .filter(ann => ann.rate.some(r => r.stato !== 'futuro'))
              .reverse()
              .map(ann => {
              const isExpanded = expandedAnnualita[ann.numero] || false;
              // Totali per rate concluse + pagate (entrambe chiuse)
              const sddConcluso = ann.rate.filter(r => r.stato === 'concluso' || r.stato === 'pagato').reduce((s, r) => s + r.sdd_amount, 0);
              const sepaConcluso = ann.rate.filter(r => r.stato === 'concluso' || r.stato === 'pagato').reduce((s, r) => s + r.sepa_amount, 0);
              const margineConcluso = Math.round((sddConcluso - sepaConcluso) * 100) / 100;
              return (
                <div key={ann.numero} className="border rounded-lg overflow-hidden" data-testid={`annualita-${ann.numero}`}>
                  {/* Header Annualità - cliccabile per espandere/collassare */}
                  <button
                    className="w-full bg-gradient-to-r from-slate-700 to-slate-600 text-white px-4 py-2.5 flex items-center justify-between hover:from-slate-600 hover:to-slate-500 transition-colors"
                    onClick={() => setExpandedAnnualita(prev => ({ ...prev, [ann.numero]: !prev[ann.numero] }))}
                    data-testid={`annualita-toggle-${ann.numero}`}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <h3 className="font-bold text-base">{ann.label}</h3>
                      <span className="text-xs text-slate-300 ml-2">({ann.num_rate} mensilità)</span>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <span>SDD IN: <strong>{sddConcluso.toLocaleString('it-IT', { minimumFractionDigits: 2 })}€</strong></span>
                      <span>SEPA OUT: <strong>{sepaConcluso.toLocaleString('it-IT', { minimumFractionDigits: 2 })}€</strong></span>
                      <span className={margineConcluso >= 0 ? 'text-green-300' : 'text-red-300'}>
                        Margine: <strong>{margineConcluso.toLocaleString('it-IT', { minimumFractionDigits: 2 })}€</strong>
                      </span>
                      <Badge className="bg-slate-500 text-white text-xs">{(ann.num_concluse || 0) + (ann.num_pagate || 0)}/{ann.num_rate} concluse/pagate</Badge>
                    </div>
                  </button>
                  
                  {/* Tabella rate - visibile solo se espansa */}
                  {isExpanded && (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="text-xs bg-slate-50">
                            <TableHead className="py-2 font-semibold w-16">Mens.</TableHead>
                            <TableHead className="py-2 font-semibold">Mese</TableHead>
                            <TableHead className="text-right py-2 font-semibold">Canone</TableHead>
                            <TableHead className="text-right py-2 font-semibold">Condom.</TableHead>
                            <TableHead className="text-right py-2 font-semibold">Bollo</TableHead>
                            <TableHead className="text-right py-2 font-semibold">SA Inq.</TableHead>
                            <TableHead className="text-right py-2 font-semibold">SA Prop.</TableHead>
                            <TableHead className="text-right py-2 font-semibold text-red-600">Sconto</TableHead>
                            <TableHead className="text-right py-2 font-semibold text-blue-700">SDD IN</TableHead>
                            <TableHead className="text-right py-2 font-semibold text-green-700">SEPA OUT</TableHead>
                            <TableHead className="py-2 text-center font-semibold">Stato</TableHead>
                            <TableHead className="py-2 text-center font-semibold">
                              <span title="Verde=PDF ok | Ambra=CSV ok, PDF mancante | Rosso=tutto mancante">Fatt.</span>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {ann.rate.map(rata => {
                            const comp = rata.components || {};
                            const isFuturo = rata.stato === 'futuro';
                            const isProsecuzione = !!rata.prosecuzione_tipo;
                            const txtCls = isFuturo ? 'text-slate-300' : '';
                            const fmt = (v) => v ? v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00';
                            
                            const statoBadge = {
                              concluso: { cls: 'bg-green-100 text-green-700', label: 'Concluso' },
                              pagato: { cls: 'bg-emerald-100 text-emerald-700', label: 'Pagato' },
                              parziale: { cls: 'bg-blue-100 text-blue-700', label: 'Parziale' },
                              da_pagare: { cls: 'bg-amber-100 text-amber-700', label: 'Da pagare' },
                              futuro: { cls: 'bg-slate-50 text-slate-300 border border-slate-200', label: 'Futuro' },
                            }[rata.stato] || { cls: 'bg-slate-100 text-slate-500', label: rata.stato };

                            const rowKey = rata.rata_label ? `${rata.rata_numero}-${rata.rata_label}` : rata.rata_numero;
                            const prosecBg = isProsecuzione && rata.prosecuzione_tipo === 'precedente' ? 'bg-amber-50/60' : '';

                            return (
                              <TableRow key={rowKey} className={`text-sm ${isFuturo ? 'bg-slate-50/40' : prosecBg || 'hover:bg-slate-100'}`} data-testid={`storico-row-${rowKey}`}>
                                <TableCell className={`py-1.5 font-mono text-xs font-bold text-center ${txtCls}`}>
                                  {rata.rata_label || rata.rata_numero}/{rata.rata_totale}
                                  {rata.percentuale !== 100 && (
                                    <span className={`block text-[10px] ${isFuturo ? 'text-slate-300' : 'text-orange-500'}`}>{rata.percentuale}%</span>
                                  )}
                                  {isProsecuzione && (
                                    <span className={`block text-[9px] font-normal ${rata.prosecuzione_tipo === 'precedente' ? 'text-amber-600' : 'text-blue-600'}`}>
                                      {rata.prosecuzione_tipo === 'precedente' ? 'prosec.' : 'nuovo'}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className={`py-1.5 font-medium text-sm ${txtCls}`}>
                                  {new Date(rata.month).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                                </TableCell>
                                <TableCell className={`text-right py-1.5 tabular-nums ${txtCls}`}>{fmt(comp.canone)}</TableCell>
                                <TableCell className={`text-right py-1.5 tabular-nums ${txtCls}`}>{fmt(comp.condominio)}</TableCell>
                                <TableCell className={`text-right py-1.5 tabular-nums ${txtCls}`}>{fmt(comp.bollo)}</TableCell>
                                <TableCell className={`text-right py-1.5 tabular-nums ${txtCls}`}>{fmt(comp.sa_inquilino)}</TableCell>
                                <TableCell className={`text-right py-1.5 tabular-nums ${txtCls}`}>{fmt(comp.sa_proprietario)}</TableCell>
                                <TableCell className={`text-right py-1.5 tabular-nums ${isFuturo ? 'text-slate-300' : comp.sconto > 0 ? 'text-red-600' : 'text-slate-300'}`}>
                                  {comp.sconto > 0 ? `-${fmt(comp.sconto)}` : '-'}
                                </TableCell>
                                {/* SDD, SEPA, Stato, Fatt: per prosecuzione 1b non renderizzare (rowSpan su 1a) */}
                                {!(isProsecuzione && rata.prosecuzione_tipo === 'precedente') && (
                                  <>
                                    <TableCell rowSpan={isProsecuzione && rata.prosecuzione_tipo === 'nuovo' ? 2 : 1} className={`text-right py-1.5 font-semibold tabular-nums ${isFuturo ? 'text-slate-300' : 'text-blue-700'} ${isProsecuzione && rata.prosecuzione_tipo === 'nuovo' ? 'align-middle border-l border-slate-100' : ''}`}>
                                      {isProsecuzione && rata.prosecuzione_tipo === 'nuovo' ? fmt(rata.sdd_amount * 2) : fmt(rata.sdd_amount)}
                                    </TableCell>
                                    <TableCell rowSpan={isProsecuzione && rata.prosecuzione_tipo === 'nuovo' ? 2 : 1} className={`text-right py-1.5 font-semibold tabular-nums ${isFuturo ? 'text-slate-300' : 'text-green-700'} ${isProsecuzione && rata.prosecuzione_tipo === 'nuovo' ? 'align-middle' : ''}`}>
                                      {isProsecuzione && rata.prosecuzione_tipo === 'nuovo' ? fmt(rata.sepa_amount * 2) : fmt(rata.sepa_amount)}
                                    </TableCell>
                                    <TableCell rowSpan={isProsecuzione && rata.prosecuzione_tipo === 'nuovo' ? 2 : 1} className={`py-1.5 text-center ${isProsecuzione && rata.prosecuzione_tipo === 'nuovo' ? 'align-middle' : ''}`}>
                                      <Badge className={`text-[10px] px-1.5 ${statoBadge.cls}`} data-testid={`stato-${rowKey}`}>
                                        {statoBadge.label}
                                      </Badge>
                                    </TableCell>
                                    <TableCell rowSpan={isProsecuzione && rata.prosecuzione_tipo === 'nuovo' ? 2 : 1} className={`py-1.5 text-center ${isProsecuzione && rata.prosecuzione_tipo === 'nuovo' ? 'align-middle' : ''}`}>
                                      <div className="flex items-center gap-1 justify-center">
                                        {rata.sdd_id && (
                                          rata.fattura_conduttore_has_pdf ? (
                                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0" 
                                              title={`Scarica fattura conduttore: ${rata.fattura_conduttore_filename || ''}`} 
                                              onClick={() => handleDownloadFattura(rata.sdd_id, 'conduttore')}
                                              data-testid={`download-fattura-cond-${rata.rata_numero}`}>
                                              <Download className="w-3 h-3 text-blue-600" />
                                            </Button>
                                          ) : rata.fattura_conduttore_numero ? (
                                            <label className="cursor-pointer inline-flex items-center justify-center w-5 h-5 rounded bg-amber-100" 
                                              title={`${rata.fattura_conduttore_numero} — PDF da caricare`}>
                                              <input type="file" accept=".pdf" className="hidden" 
                                                data-testid={`upload-fattura-cond-${rata.rata_numero}`}
                                                onChange={(e) => { handleUploadFattura(rata.sdd_id, e.target.files[0], 'conduttore'); e.target.value = ''; }} />
                                              <Upload className="w-3 h-3 text-amber-600" />
                                            </label>
                                          ) : (
                                            <label className="cursor-pointer inline-flex items-center justify-center w-5 h-5 rounded bg-red-50" 
                                              title="CSV + PDF mancanti">
                                              <input type="file" accept=".pdf" className="hidden" 
                                                data-testid={`upload-fattura-cond-empty-${rata.rata_numero}`}
                                                onChange={(e) => { handleUploadFattura(rata.sdd_id, e.target.files[0], 'conduttore'); e.target.value = ''; }} />
                                              <Upload className="w-3 h-3 text-red-400" />
                                            </label>
                                          )
                                        )}
                                        {rata.sdd_id && (rata.fattura_conduttore_has_pdf || rata.fattura_conduttore_numero) && (
                                          <button type="button"
                                            className="inline-flex items-center justify-center w-5 h-5 rounded text-red-400 hover:text-red-600"
                                            title="Cancella fattura conduttore da questa rata"
                                            data-testid={`delete-fattura-cond-${rata.rata_numero}`}
                                            onClick={() => handleDeleteFatturaStorico(rata.sdd_id, 'conduttore', rata.fattura_conduttore_numero)}>
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        )}
                                        {rata.sdd_id && rata.fattura_conduttore_fic_id && !rata.fattura_conduttore_has_pdf && (
                                          <button type="button"
                                            className="inline-flex items-center justify-center w-5 h-5 rounded text-blue-500 hover:text-blue-700"
                                            title="Importa PDF e stato SDI da Fatture in Cloud (conduttore)"
                                            data-testid={`importa-fic-cond-${rata.rata_numero}`}
                                            onClick={() => handleImportaFatturaFic(rata.sdd_id)}>
                                            <FileText className="w-3 h-3" />
                                          </button>
                                        )}
                                        {rata.fattura_conduttore_ei_status && (
                                          <span className="text-[9px] px-1 rounded bg-slate-100 text-slate-600" title="Stato SDI conduttore">
                                            {rata.fattura_conduttore_ei_status}
                                          </span>
                                        )}
                                        {rata.sepa_id && (
                                          rata.fattura_locatore_has_pdf ? (
                                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0" 
                                              title={`Scarica fattura locatore: ${rata.fattura_locatore_filename || ''}`} 
                                              onClick={() => handleDownloadFattura(rata.sepa_id, 'locatore')}
                                              data-testid={`download-fattura-loc-${rata.rata_numero}`}>
                                              <Download className="w-3 h-3 text-green-600" />
                                            </Button>
                                          ) : rata.fattura_locatore_numero ? (
                                            <label className="cursor-pointer inline-flex items-center justify-center w-5 h-5 rounded bg-amber-100" 
                                              title={`${rata.fattura_locatore_numero} — PDF da caricare`}>
                                              <input type="file" accept=".pdf" className="hidden" 
                                                data-testid={`upload-fattura-loc-${rata.rata_numero}`}
                                                onChange={(e) => { handleUploadFattura(rata.sepa_id, e.target.files[0], 'locatore'); e.target.value = ''; }} />
                                              <Upload className="w-3 h-3 text-amber-600" />
                                            </label>
                                          ) : (
                                            <label className="cursor-pointer inline-flex items-center justify-center w-5 h-5 rounded bg-red-50" 
                                              title="CSV + PDF mancanti">
                                              <input type="file" accept=".pdf" className="hidden" 
                                                data-testid={`upload-fattura-loc-empty-${rata.rata_numero}`}
                                                onChange={(e) => { handleUploadFattura(rata.sepa_id, e.target.files[0], 'locatore'); e.target.value = ''; }} />
                                              <Upload className="w-3 h-3 text-red-400" />
                                            </label>
                                          )
                                        )}
                                        {rata.sepa_id && (rata.fattura_locatore_has_pdf || rata.fattura_locatore_numero) && (
                                          <button type="button"
                                            className="inline-flex items-center justify-center w-5 h-5 rounded text-red-400 hover:text-red-600"
                                            title="Cancella fattura locatore da questa rata"
                                            data-testid={`delete-fattura-loc-${rata.rata_numero}`}
                                            onClick={() => handleDeleteFatturaStorico(rata.sepa_id, 'locatore', rata.fattura_locatore_numero)}>
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        )}
                                        {rata.sepa_id && rata.fattura_locatore_fic_id && !rata.fattura_locatore_has_pdf && (
                                          <button type="button"
                                            className="inline-flex items-center justify-center w-5 h-5 rounded text-blue-500 hover:text-blue-700"
                                            title="Importa PDF e stato SDI da Fatture in Cloud (locatore)"
                                            data-testid={`importa-fic-loc-${rata.rata_numero}`}
                                            onClick={() => handleImportaFatturaFic(rata.sepa_id)}>
                                            <FileText className="w-3 h-3" />
                                          </button>
                                        )}
                                        {rata.fattura_locatore_ei_status && (
                                          <span className="text-[9px] px-1 rounded bg-slate-100 text-slate-600" title="Stato SDI locatore">
                                            {rata.fattura_locatore_ei_status}
                                          </span>
                                        )}
                                        {!rata.sdd_id && !rata.sepa_id && (
                                          <>
                                            <label className="cursor-pointer inline-flex items-center justify-center w-5 h-5 rounded bg-red-50"
                                              title="Fattura /CO mancante — carica PDF (crea la rata)">
                                              <input type="file" accept=".pdf" className="hidden"
                                                data-testid={`upload-fattura-cond-nuovo-${rata.rata_numero}`}
                                                onChange={(e) => { handleUploadFatturaMese(rata, 'conduttore', e.target.files[0]); e.target.value = ''; }} />
                                              <Upload className="w-3 h-3 text-red-400" />
                                            </label>
                                            <label className="cursor-pointer inline-flex items-center justify-center w-5 h-5 rounded bg-red-50"
                                              title="Fattura /LO mancante — carica PDF (crea la rata)">
                                              <input type="file" accept=".pdf" className="hidden"
                                                data-testid={`upload-fattura-loc-nuovo-${rata.rata_numero}`}
                                                onChange={(e) => { handleUploadFatturaMese(rata, 'locatore', e.target.files[0]); e.target.value = ''; }} />
                                              <Upload className="w-3 h-3 text-red-400" />
                                            </label>
                                          </>
                                        )}
                                      </div>
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Sezione Movimenti Straordinari */}
            {storicoPagamentiData.straordinari && storicoPagamentiData.straordinari.length > 0 && (
              <div className="border rounded-lg overflow-hidden" data-testid="straordinari-section">
                <button
                  className="w-full bg-gradient-to-r from-amber-700 to-amber-600 text-white px-4 py-2.5 flex items-center justify-between hover:from-amber-600 hover:to-amber-500 transition-colors"
                  onClick={() => setExpandedAnnualita(prev => ({ ...prev, straordinari: !prev.straordinari }))}
                  data-testid="straordinari-toggle"
                >
                  <div className="flex items-center gap-2">
                    {expandedAnnualita.straordinari ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <h3 className="font-bold text-base">Movimenti Straordinari</h3>
                    <span className="text-xs text-amber-200 ml-2">({storicoPagamentiData.straordinari.length} movimenti)</span>
                  </div>
                  <div className="flex gap-4 text-xs">
                    <span>Totale: <strong>{storicoPagamentiData.straordinari.reduce((s, m) => s + (m.importo || m.sdd_amount || 0), 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}€</strong></span>
                  </div>
                </button>

                {expandedAnnualita.straordinari && (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs bg-amber-50">
                          <TableHead className="py-2 font-semibold">Data</TableHead>
                          <TableHead className="py-2 font-semibold">N° Fattura</TableHead>
                          <TableHead className="py-2 font-semibold">Destinatario</TableHead>
                          <TableHead className="text-right py-2 font-semibold">Importo</TableHead>
                          <TableHead className="py-2 font-semibold">Categoria</TableHead>
                          <TableHead className="py-2 font-semibold">Note</TableHead>
                          <TableHead className="py-2 text-center font-semibold">SDI</TableHead>
                          <TableHead className="py-2 text-center font-semibold">PDF</TableHead>
                          <TableHead className="py-2 text-center font-semibold w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {storicoPagamentiData.straordinari.map((mov, idx) => (
                          <TableRow key={mov.id || idx} className="text-sm hover:bg-amber-50/50" data-testid={`straordinario-row-${idx}`}>
                            <TableCell className="py-1.5 font-medium whitespace-nowrap">
                              {mov.fattura_data || (mov.data ? new Date(mov.data).toLocaleDateString('it-IT') : (mov.month ? new Date(mov.month).toLocaleDateString('it-IT') : '-'))}
                            </TableCell>
                            <TableCell className="py-1.5 font-mono text-xs">{mov.fattura_numero || '-'}</TableCell>
                            <TableCell className="py-1.5 text-sm max-w-[200px] truncate">{mov.destinatario || '-'}</TableCell>
                            <TableCell className="text-right py-1.5 font-semibold tabular-nums text-amber-700 whitespace-nowrap">
                              {(mov.importo || mov.sdd_amount || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                            </TableCell>
                            <TableCell className="py-1.5">
                              <select
                                className="text-xs border rounded px-1.5 py-1 bg-white w-full min-w-[140px]"
                                value={mov.straordinario_tipo || ''}
                                data-testid={`straordinario-tipo-${idx}`}
                                onChange={async (e) => {
                                  const val = e.target.value;
                                  try {
                                    await axios.patch(`${API_URL}/payments/${mov.id}/straordinario`, { straordinario_tipo: val });
                                    // Update local state
                                    setStoricoPagamentiData(prev => ({
                                      ...prev,
                                      straordinari: prev.straordinari.map(s => s.id === mov.id ? { ...s, straordinario_tipo: val } : s)
                                    }));
                                    toast.success('Categoria aggiornata');
                                  } catch { toast.error('Errore aggiornamento'); }
                                }}
                              >
                                <option value="">-- Seleziona --</option>
                                <option value="imposta_registro">Imposta di Registro</option>
                                <option value="imposta_registro_annualita">Imp. Registro Annualità</option>
                                <option value="conguaglio_condominio">Conguaglio Condominio</option>
                                <option value="intervento_riparazione">Intervento/Riparazione</option>
                                <option value="cauzione">Cauzione</option>
                                <option value="penale">Penale</option>
                                <option value="rimborso">Rimborso</option>
                                <option value="altro">Altro</option>
                              </select>
                            </TableCell>
                            <TableCell className="py-1.5">
                              <input
                                type="text"
                                className="text-xs border rounded px-1.5 py-1 bg-white w-full min-w-[120px]"
                                placeholder="Note..."
                                defaultValue={mov.straordinario_descrizione || mov.note || ''}
                                data-testid={`straordinario-note-${idx}`}
                                onBlur={async (e) => {
                                  const val = e.target.value.trim();
                                  const prev = mov.straordinario_descrizione || mov.note || '';
                                  if (val === prev) return;
                                  try {
                                    await axios.patch(`${API_URL}/payments/${mov.id}/straordinario`, { straordinario_descrizione: val });
                                    setStoricoPagamentiData(prevData => ({
                                      ...prevData,
                                      straordinari: prevData.straordinari.map(s => s.id === mov.id ? { ...s, straordinario_descrizione: val } : s)
                                    }));
                                    toast.success('Note aggiornate');
                                  } catch { toast.error('Errore aggiornamento'); }
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              />
                            </TableCell>
                            <TableCell className="py-1.5 text-center text-xs text-slate-500">{mov.fattura_sdi || '-'}</TableCell>
                            <TableCell className="py-1.5 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {mov.has_pdf ? (
                                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0"
                                    title="Scarica PDF"
                                    onClick={() => handleDownloadFattura(mov.id, 'straordinario')}
                                    data-testid={`download-straordinario-${idx}`}>
                                    <Download className="w-3 h-3 text-amber-600" />
                                  </Button>
                                ) : (
                                  <span className="text-xs text-slate-300">-</span>
                                )}
                                {(mov.has_pdf || mov.fattura_numero) && (
                                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-orange-400 hover:text-orange-600"
                                    title="Rimuovi la fattura da questo movimento (mantiene il movimento)"
                                    data-testid={`delete-fattura-straordinario-${idx}`}
                                    onClick={() => handleDeleteFatturaStorico(mov.id, 'straordinario', mov.fattura_numero)}>
                                    <FileX className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="py-1.5 text-center">
                              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600"
                                title="Elimina movimento"
                                data-testid={`delete-straordinario-${idx}`}
                                onClick={async () => {
                                  if (!window.confirm(`Eliminare il movimento ${mov.fattura_numero || ''} del ${mov.fattura_data || ''}?`)) return;
                                  try {
                                    await axios.delete(`${API_URL}/payments/${mov.id}/straordinario`);
                                    setStoricoPagamentiData(prev => ({
                                      ...prev,
                                      straordinari: prev.straordinari.filter(s => s.id !== mov.id)
                                    }));
                                    toast.success('Movimento eliminato');
                                  } catch { toast.error('Errore eliminazione'); }
                                }}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStoricoPagamentiDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContrattiPage;
